/**
 * shared/types.ts — Pear protocol contract (AUTHORITATIVE, parent-owned).
 *
 * Children implement AGAINST this file and must never redefine anything in it.
 * If a child believes the contract needs a change: stop and report it — the
 * parent is the single author. Owned by W0 (parent). Do not edit.
 *
 * Covers: invite-code format + expiry, couple/member model, token format,
 * WebSocket message envelope, Durable Object method signatures, SQLite schema.
 */

export const PROTOCOL_VERSION = 1 as const;

/** Phase B (card YgY8NtJjIioR) — type-only so the runtime graph stays acyclic:
 *  date-places imports VALUES from date-prefs, which imports types from here. */
import type { GeoPoint, PlaceCandidate, PlaceSkipReason, PlaceSearchQuery } from './date-places.ts';
import type { DateNudgeResult } from './date-nudge.ts';
import { MAX_THREAD_TEXT } from './security.ts';
import type { ConciergeTurnResult, Entitlement, EntitlementRecord, SubscriptionUpdate } from './billing.ts';
/** Fairness engine (card S1YSeaemeA2N): type-only, so the runtime graph
 *  stays acyclic the same way date-places does it. */
import type { FairnessEvaluation } from './fairness.ts';

/* ------------------------------------------------------------------ *
 * 1. Invite codes — format + expiry rules
 * ------------------------------------------------------------------ */

/** 29 chars, Crockford-ish: no 0/O, 1/I/L, no U or S (avoids accidental words). */
export const INVITE_ALPHABET = 'ABCDEFGHJKMNPQRTVWXYZ23456789';
export const INVITE_LENGTH = 6;
/**
 * An invite code is valid for 24 hours from creation.
 *
 * Jonathan, 2026-10-23 (card HpgPwz4VbBRa): "Yes 24h + new code". Ten minutes
 * only ever suited a code read off a screen; the moment an invite goes out by
 * email or a message the partner opens it the next morning, and the TTL was
 * routinely dead before anyone saw it. The 24h window is re-armed whenever the
 * invite is MAILED (handleEmailCode), so the clock starts when the invite lands,
 * and the in-app "Get a fresh code" action covers anything later.
 *
 * The trade-off is stated plainly: a 6-char code in a 29-char alphabet lives
 * 24 hours instead of 10 minutes. Joining still needs an account and the join
 * route keeps its per-IP rate limit, so the guess space is bounded by requests
 * rather than by the clock — but the window is now the reason to keep that
 * limit tight, not a backstop that made it optional.
 */
export const INVITE_TTL_MS = 24 * 60 * 60 * 1000;
/**
 * Card tJdisOEkVwha — the emailed invite LINK lives 24 hours.
 *
 * The code and the link share one 24-hour window (card HpgPwz4VbBRa, decision
 * (a) 2026-10-24 — 24h everywhere, because Copy and Share links carry the code
 * itself, so a short code window kills every handed-over invite). The link is single-use, so a long life costs
 * nothing: it admits one person once and is spent.
 */
export const INVITE_LINK_TTL_MS = 24 * 60 * 60 * 1000;
/** A couple holds exactly two members. */
export const COUPLE_MAX_MEMBERS = 2;

export function isValidInviteCode(code: string): boolean {
  if (typeof code !== 'string' || code.length !== INVITE_LENGTH) return false;
  for (const ch of code) if (!INVITE_ALPHABET.includes(ch)) return false;
  return true;
}

/**
 * coupleId is DERIVED from the invite code — there is no code→couple index
 * anywhere, so there are no shared tables across couples (tenancy rule).
 * The Durable Object instance name IS the coupleId.
 */
export function coupleIdFromCode(code: string): string {
  return 'c_' + code;
}

/** How many fresh codes the worker tries before giving up on `code_collision`. */
export const INVITE_MAX_ATTEMPTS = 5;

/**
 * Cryptographically random invite code over INVITE_ALPHABET.
 * 29 chars is not a power of two, so a plain `byte % 29` would favour the first
 * (256 % 29) characters — rejection sampling keeps every code equally likely.
 */
export function generateInviteCode(): string {
  const chars = INVITE_ALPHABET;
  const n = chars.length;
  const limit = 256 - (256 % n); // discard the biased tail
  let out = '';
  while (out.length < INVITE_LENGTH) {
    const byte = new Uint8Array(1);
    crypto.getRandomValues(byte);
    if (byte[0] >= limit) continue;
    out += chars[byte[0] % n];
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * 2. Couple / member model
 * ------------------------------------------------------------------ */

/** Partner A creates the couple; Partner B joins. Drives the two-ness UI. */
export type Role = 'A' | 'B';

export interface Member {
  memberId: string;      // uuid v4, assigned by the DO on join
  role: Role;
  displayName: string;   // free text, may be ''
  /** The colour this member picked for their reply text (card 9QADJv3CdiOh):
   *  one of REPLY_COLOUR_KEYS, or absent/null when they never chose. The
   *  partner's Thread reads it off this list — same source as displayName,
   *  so a change shows live on both sockets. Optional because the pair-create
   *  path builds a Member straight after the INSERT, before any pick exists. */
  replyColour?: string | null;
  joinedAt: number;      // epoch ms
}

/**
 * Reply-text colours (card 9QADJv3CdiOh, founder email 2026-10-23): the sender
 * picks one in Settings > You and their words wear it in the partner's feed.
 *
 * The text lands on the partner bubble's mustard ground (#D1A23B), so every
 * entry is a DARK ink measured >= 4.5:1 against mustard — AA for body text.
 * The list is closed on purpose: the DO validates against these keys, so a
 * client cannot post an arbitrary hex that fails contrast. The picker, the
 * server's reason and the Thread fallback all read this one list.
 */
export interface ReplyColour {
  key: string;
  label: string;
  /** The measured ink. Contrast on #D1A23B noted per entry (all >= 4.5:1). */
  hex: string;
}

export const REPLY_COLOURS: readonly ReplyColour[] = [
  { key: 'bottle-green', label: 'Bottle green', hex: '#1F3B2D' }, // 5.19:1 — the current ink, kept as the default
  { key: 'ink',          label: 'Ink',          hex: '#111827' }, // 7.55:1
  { key: 'espresso',     label: 'Espresso',     hex: '#3E2723' }, // 5.89:1
  { key: 'wine',         label: 'Wine',         hex: '#4C1D2E' }, // 5.87:1
  { key: 'navy',         label: 'Navy',         hex: '#1C2E5C' }, // 5.61:1
  { key: 'midnight',     label: 'Midnight',     hex: '#16324F' }, // 5.58:1
  { key: 'charcoal',     label: 'Charcoal',     hex: '#2F3136' }, // 5.54:1
  { key: 'oxblood',      label: 'Oxblood',      hex: '#5C1A1B' }, // 5.52:1
  { key: 'pine',         label: 'Pine',         hex: '#1A3A2A' }, // 5.31:1
  { key: 'chocolate',    label: 'Chocolate',    hex: '#4A2E19' }, // 5.27:1
  { key: 'plum',         label: 'Plum',         hex: '#5B1F3D' }, // 5.23:1
  { key: 'deep-teal',    label: 'Deep teal',    hex: '#0B3D3D' }, // 5.11:1
  { key: 'denim',        label: 'Denim',        hex: '#1E3A5F' }, // 4.90:1
  { key: 'indigo',       label: 'Indigo',       hex: '#312E81' }, // 4.86:1
  { key: 'maroon',       label: 'Maroon',       hex: '#701A1A' }, // 4.83:1
  { key: 'burgundy',     label: 'Burgundy',     hex: '#6E1E2C' }, // 4.74:1
] as const;

export const REPLY_COLOUR_KEYS: readonly string[] = REPLY_COLOURS.map((c) => c.key);

export function isReplyColourKey(value: unknown): value is string {
  return typeof value === 'string' && (REPLY_COLOUR_KEYS as readonly string[]).includes(value);
}

/** The hex for a stored key, or null when the key is unknown (old row, bad
 *  write) — the client falls back to the role token rather than painting
 *  something unmeasured. */
export function replyColourHex(key: string | null | undefined): string | null {
  if (!key) return null;
  return REPLY_COLOURS.find((c) => c.key === key)?.hex ?? null;
}

export interface Couple {
  coupleId: string;       // coupleIdFromCode(code)
  code: string;           // the 6-char invite code
  createdAt: number;      // epoch ms
  expiresAt: number;      // createdAt + INVITE_TTL_MS — invite expiry, not couple expiry
  pairedAt: number | null; // epoch ms when the 2nd member joined, null while open
  /** Card Vf23eSKKxDhX — the tombstone: epoch ms when the LAST member left.
   *  Null while the room is open. A closed couple keeps every row it ever
   *  held (nothing is deleted on leave) but refuses join and refreshInvite,
   *  so its old invite code can never re-admit anyone. */
  closedAt: number | null;
  members: Member[];      // length 1 (open) or 2 (paired). NEVER > 2.
}

/**
 * The milestone line the ceremonial pairing moment shows AND the memory it
 * files in the Vault (card 2NuQ8dd2JMqs) — "You're in 🍐 · Oct 2026".
 * Computed from the clock rather than stored: it is stamped at the moment the
 * couple completes, so it is right for every couple and needs no schema.
 * The web bundle keeps its own copy (web/src/components/PairCeremony.tsx),
 * same convention as INVITE_ALPHABET.
 */
export function pairMilestone(now: Date = new Date()): string {
  const month = now.toLocaleDateString('en-AU', { month: 'short' });
  return `You're in 🍐 · ${month} ${now.getFullYear()}`;
}

/** Result of a successful join — returned by POST /api/pair/join. */
export interface JoinResult {
  couple: Couple;
  memberId: Member['memberId'];
  role: Role;
  token: string; // see §3
  /** Ceremonial pairing moment (card 2NuQ8dd2JMqs). True ONLY on the join
   *  that completed the couple — the second member's own response is what
   *  tells that device to celebrate, because the room's `paired` broadcast
   *  fires before this response and reaches only sockets already attached
   *  (the joiner has no socket yet). Never set on create or on a re-read. */
  justPaired?: boolean;
}

/**
 * Card Vf23eSKKxDhX — what a leave left behind.
 *
 * `members` is who is still in the room (length 1 = the survivor can re-invite,
 * length 0 = the room took the tombstone). `closed` mirrors that in one word
 * so the route does not have to count, and `couple` is the room as it now
 * stands — the survivor's client re-reads it straight from this response
 * rather than paying a second /api/pair/status round trip.
 */
export interface RemoveMemberResult {
  members: Member[];
  closed: boolean;
  couple: Couple;
  /** How many of the leaver's inbound open requests were closed by the exit
   *  (card uTudCJtABptq). 0 when none were open. */
  closedRequests: number;
  /** Display name of the member who just left (card ZyfsefNk8p3J), captured
   *  before their row was deleted so the survivor's line can name them. Null
   *  on the degenerate no-member-pointer path. */
  leaverName: string | null;
}

/* ------------------------------------------------------------------ *
 * 3. Token format — stateless routing to the right DO
 * ------------------------------------------------------------------ */

/**
 * token = "pear1_" + coupleId + "_" + memberId + "_" + secret
 * secret = 32 random bytes hex (crypto.getRandomValues).
 * The worker parses coupleId out of the token to pick the DO; the DO is the
 * only party that knows whether `secret` matches that member.
 */
export const TOKEN_PREFIX = 'pear1_';

export interface ParsedToken {
  coupleId: string;
  memberId: string;
  secret: string;
}

export function encodeToken(coupleId: string, memberId: string, secret: string): string {
  return `${TOKEN_PREFIX}${coupleId}_${memberId}_${secret}`;
}

export function parseToken(token: string): ParsedToken | null {
  if (!token.startsWith(TOKEN_PREFIX)) return null;
  const rest = token.slice(TOKEN_PREFIX.length);
  const secretSep = rest.lastIndexOf('_');
  const memberSep = rest.lastIndexOf('_', secretSep - 1);
  if (secretSep < 0 || memberSep < 0) return null;
  const coupleId = rest.slice(0, memberSep);
  const memberId = rest.slice(memberSep + 1, secretSep);
  const secret = rest.slice(secretSep + 1);
  if (!coupleId.startsWith('c_') || !memberId || !secret) return null;
  return { coupleId, memberId, secret };
}

/* ------------------------------------------------------------------ *
 * 4. WebSocket message envelope
 * ------------------------------------------------------------------ */

export type ErrorCode =
  | 'illegal_transition'
  | 'not_revealed'
  | 'invalid_code'
  | 'expired_code'
  | 'code_consumed'
  | 'code_collision'
  | 'couple_full'
  /* Card Vf23eSKKxDhX — the one-way door has a door now. `couple_closed` is
     the tombstone: the room's last member left, so its old invite code is
     refused rather than silently re-admitted. `not_in_couple` names the
     account that asked to leave something it does not belong to. */
  | 'couple_closed'
  | 'not_in_couple'
  /* card j1pvawhlLBks: refreshInvite() refuses once the couple is complete */
  | 'already_paired'
  /* Card tJdisOEkVwha — the emailed 24h link. Three distinct answers, because
     "it did not work" is the one thing the partner cannot act on: the link is
     unknown/malformed, already used (single-use), or past its 24 hours. */
  | 'link_invalid'
  | 'link_used'
  | 'link_expired'
  | 'bad_token'
  | 'bad_payload'
  | 'not_found'
  /* Invite-only (Jonathan, 2026-10-23): PEAR_SIGNUP_MODE is not 'open', so no
     new account may be created — on /api/auth/register or through the Google
     callback. Sign-in for existing accounts is unaffected. */
  | 'signups_closed'
  /* Card Q4BluQX8fXgj — only the creator may remove an entry, shared or not:
     a partner's delete is refused with the reason named, not swallowed. */
  | 'forbidden'
  /* the gifting agent is behind the per-category AI toggle (card Hsk5cyJ8ap1R) */
  | 'agent_off'
  /* kudos on a request row (card HBYCnnD9q5FS): who may praise what */
  | 'self_kudos'
  | 'already_given'
  /* Settings names (card yFLCjm7ONIEc): the specific reason, not a generic
     failure — the Settings status line prints it verbatim. */
  | 'name_required'
  | 'name_too_long'
  | 'display_name_required'
  | 'display_name_too_long'
  /* Reply-text colour (card 9QADJv3CdiOh): the key was outside the offered
     16 — the Settings status line prints the reason verbatim. */
  | 'bad_reply_colour'
  /* Shared handle (card w8Mj4ThRii6Z) — same rule as the names: the Settings
     status line prints the reason verbatim, so the code names the reason. */
  | 'handle_required'
  | 'handle_bad'
  /* Inbox content routing (card 99VNKrT2nwvy) — the move affordance names
     the reason verbatim, same contract as the Settings names. */
  | 'already_filed'
  | 'quarantined'
  | 'no_link_to_file'
  /* Chore intake (card UZDxtHiJhtRt) — the gate names its own failures. */
  | 'no_pending_proposal'
  | 'already_decided'
  /* Re-route (card NWmSqIsojQyU) — a private message never moves, and a
     target that is not a member of this couple is not a destination. */
  | 'private_item'
  | 'unknown_member'
  | 'handle_taken'
  /* Over-length entry at any door (card YYrJc5OkW7EP): the specific reason,
     so the composer can say "too long" instead of "something went wrong". */
  | 'too_long'
  /* Guess Me round engine (card eq92fFXqQUuP) — the state machine names every
     refusal, so the screen can say which of them it was: the day's three
     rounds are used, there is no round in the state you acted on, it is not
     your role, your submission is already locked, the guess landed before
     the choices existed, or the couple is not paired. */
  | 'cap_reached'
  | 'not_open'
  | 'not_your_turn'
  | 'already_locked'
  | 'too_late'
  | 'not_scoreable'
  | 'not_paired'
  /* Meeting transcription (card a0ABwZvTf9ct): the specific failure, so the
     capture sheet can say what happened instead of "something went wrong". */
  | 'stt_unavailable'
  | 'audio_invalid'
  /* The account-to-couple transition is a compare-and-set (card AetvN3T4ZBmK):
     a concurrent link/join moved this account first, so the caller re-reads
     state and retries rather than trusting a silent overwrite. */
  | 'link_conflict'
  /* No-double-billing guard (card ImIQwJDLW6Xs): the account already belongs
     to a couple holding a live subscription, and its current couple could not
     be verified. Both name themselves so the UI can say why the join stopped. */
  | 'couple_subscription_active'
  /* Promo redemption (card yWsCXHAXQ3Ej): each refusal names itself so the
     upgrade sheet can say what happened — bad code, wrong account, or this
     couple already holds a grant. */
  | 'promo_invalid'
  | 'promo_wrong_account'
  | 'promo_already_redeemed'
  | 'couple_check_failed'
  | 'audio_too_large'
  | 'transcription_failed'
  /* Plan, the voice-first weekly meeting (card 7aTCRQ9j8PWV): the specific
     reason, so the Plan screen can say it instead of "something went wrong". */
  | 'transcript_required'
  | 'transcript_too_long'
  | 'session_not_editable'
  | 'already_confirmed'
  | 'session_discarded'
  /* Calendar drafts from a meeting (card hCF9D2WCpv7Q): an unknown proposal
     id is a missing resource; `google_not_connected` and
     `calendar_push_failed` are the two answers to "Add to calendar" that
     leave the proposal untouched so the couple can tap again. */
  | 'unknown_calendar_draft'
  | 'google_not_connected'
  | 'calendar_push_failed'
  /* Chores assignment modes (card UeOwZSHfdZCh): the partner claimed it first,
     so the caller needs the conflict named rather than a generic failure. */
  | 'already_claimed'
  /* Gift Concierge V1 T+5 reaction (card Yw5UFURMXSTY): the pick is not
     accepted yet, or has already been answered — both are client-visible
     states the reaction card must render rather than a generic failure. */
  | 'not_accepted'
  | 'already_answered'
  | 'synthesis_failed'
  | 'synthesis_unavailable'
  /* Pear Plus paywall (card 32BEnNO6I2kx): the tap landed on a Plus-only
     surface from an Essentials couple — 402, and the client opens the
     upgrade sheet instead of saying "something went wrong". The three
     Stripe-side codes name the server's own failures, never the user's. */
  | 'plus_required'
  | 'stripe_not_configured'
  | 'stripe_error'
  | 'bad_signature'
  /* Support revoke route (sol-review IsfArxwGo4FX): the ops token is unset,
     so the server simply has no support route — fail-closed, not a user error. */
  | 'support_not_configured'
  /* Web Push (card HWrVePJ5qW4d): the VAPID keys or the D1 binding are
     absent, so push cannot be offered — fail-closed, not a user error. */
  | 'push_not_configured'
  /* Referrals (card 3MWZ8Zs7jA7e): ported from JitsLab. Each refusal names
     itself so the share sheet can say what happened — the link is unknown, it
     is the couple's OWN link (no self-referral), or this couple has already
     used one. `referrals_unconfigured` is the server missing its D1 binding,
     never the user's mistake. */
  | 'referral_unknown'
  | 'referral_self'
  | 'referral_already'
  | 'referrals_unconfigured'
  | 'internal';

export type ClientMessage =
  | { v: 1; t: 'hello'; token: string }
  | { v: 1; t: 'send'; clientMsgId: string; text: string; wantsAgent?: boolean }
  | { v: 1; t: 'msg-remove'; seq: number }
  | { v: 1; t: 'history'; before?: number; limit?: number; sinceDays?: number }
  /* Card J9hHjSZDFTCV — this device's own visibility. `hidden` is a claim about
   * ONE socket, never about anyone else: it only ever moves the SENDER's entry
   * in the room's in-memory presence map, so a client cannot talk its partner
   * into appearing offline. */
  | { v: 1; t: 'presence'; hidden: boolean }
  /* Card tPPB3ozS4YrK — "I am typing", nothing more. No payload: the room
   * already knows who sent it, and a frame that carried text would turn an
   * ephemeral hint into a second copy of the draft. Carries no sequence, no
   * recipient and no timing — the room stamps `at` when it relays. */
  | { v: 1; t: 'typing' }
  | { v: 1; t: 'ping' };

export type ServerMessage =
  | { v: 1; t: 'welcome'; coupleId: string; memberId: string; role: Role; head: number }
  | { v: 1; t: 'msg'; message: Message }
  /* An existing message changed in place — the game round's card is rewritten
     when the round reveals (card Bkt2clm8SSOI). Same seq, new text. */
  | { v: 1; t: 'msg-update'; message: Message }
  /* An existing message is gone: a round abandoned by "next topic" takes its
     invitation card out of the thread rather than leaving a dead link. */
  | { v: 1; t: 'msg-remove'; seq: number }
  | { v: 1; t: 'ack'; clientMsgId: string; seq: number }
  | { v: 1; t: 'history'; messages: Message[]; hasMore: boolean }
  | { v: 1; t: 'error'; code: ErrorCode; message: string; ref?: string }
  /* A request row changed (kudos landed): the panel refetches on this. */
  | { v: 1; t: 'requests'; requestId: string; kudosBy: string[] }
  /* A chore row changed — a reaction landed or a done-note was written (card
   * MnorjNDwB3of). The partner's Chores panel refetches so the receipt and
   * the glyph appear without a reload, same hand-off as requests. */
  | { v: 1; t: 'chores'; choreId: string }
  /* The game round moved (answer landed / reveal): refetch. */
  | { v: 1; t: 'game'; roundId: string; state: 'open' | 'revealed' }
  /* A display name changed (card yFLCjm7ONIEc): both clients re-read
     /api/pair/status so the partner sees the new name without a reload. */
  | { v: 1; t: 'members' }
  /* Card J9hHjSZDFTCV — who is currently watching this room. Ephemeral: the
     frame is derived from the DO's socket map at the moment it is sent, is
     never persisted, and a client that goes quiet or closes drops out of the
     NEXT frame. `online` holds member ids only — no read state, no counts, no
     ticks, nothing about what anyone has seen. */
  | { v: 1; t: 'presence'; online: string[]; at: number }
  /* Card tPPB3ozS4YrK — the partner is typing in an open thread. Relay-only:
     the room stamps `at` on the way out and never keeps it, so a receiver can
     run its own clock and drop the hint 4s after it ARRIVED rather than
     trusting two devices' clocks to agree. `memberId` is the sender's, which
     is what lets the client refuse its own echo as a second belt on the
     room's partner-only relay. */
  | { v: 1; t: 'typing'; memberId: string; at: number }
  | { v: 1; t: 'pong' };

export type MessageKind = 'text' | 'agent-card' | 'kudos' | 'request-card' | 'question-card' | 'date-card' | 'system';

export interface Message {
  seq: number;            // per-couple monotonic, starts at 1, gapless
  id: string;             // uuid v4 assigned by the DO
  kind: MessageKind;
  fromMemberId: string | null; // null for 'agent-card' (third participant, Phase 0 stub)
  text: string;
  ts: number;             // epoch ms assigned by the DO (single clock = DO blockConcurrencyWhile)
  clientMsgId?: string;   // echo for sender-side de-dupe (uuid v4 from client)
  /** Set only on a game-round card: which round this message belongs to. It is
   *  how one round keeps exactly one card (post once, rewrite on reveal,
   *  delete when abandoned). Absent on every other kind of message. */
  roundId?: string;
}

/** Serialize any ServerMessage for the wire (JSON text frame). */
export function encodeServerMessage(m: ServerMessage): string {
  return JSON.stringify(m);
}

/**
 * clientMsgId prefix reserved for the SERVER (card OAcY41nByea9). The agent
 * card commits under `<AGENT_COMMIT_PREFIX><source message id>`, so
 * reconciliation can find "the card this message was promised" by id alone.
 * parseClientMessage refuses the prefix on the way in: it is the server's
 * namespace, not the client's.
 */
export const AGENT_COMMIT_PREFIX = 'agent-card:';

/**
 * clientMsgId prefix for the METERED concierge turn (card njDMEWNC4maU). The
 * reservation id is derived from the source message — `agent-turn:<msgId>` —
 * instead of a random uuid, because it is the only handle an interrupted run
 * still has: BillingSlice keeps a durable take ledger keyed by it, so the
 * retry recomputes the id, finds the take that is already charged, and gets
 * that turn back instead of paying for a second one. Server-owned, refused by
 * parseClientMessage for the same reason as AGENT_COMMIT_PREFIX.
 */
export const AGENT_TURN_PREFIX = 'agent-turn:';


/** Parse + structurally validate a client frame. Throws `Error` with the code as message. */
export function parseClientMessage(raw: string): ClientMessage {
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    throw new Error('bad_payload');
  }
  const m = obj as Partial<ClientMessage> & { v?: number; t?: string };
  if (m.v !== 1 || typeof m.t !== 'string') throw new Error('bad_payload');
  switch (m.t) {
    case 'hello':
      if (typeof (m as { token?: string }).token !== 'string') throw new Error('bad_payload');
      break;
    case 'send': {
      const s = m as { clientMsgId?: unknown; text?: unknown; wantsAgent?: unknown };
      if (typeof s.clientMsgId !== 'string' || typeof s.text !== 'string') throw new Error('bad_payload');
      // Server-owned namespaces (card OAcY41nByea9, extended by card
      // njDMEWNC4maU): the agent card commits under AGENT_COMMIT_PREFIX +
      // message id, and a METERED turn reserves under AGENT_TURN_PREFIX +
      // message id. Reconciliation reads those ids to decide whether the card
      // landed and whether the turn is still owed. a client id squatting on
      // either prefix could let a TEXT row impersonate a delivered card — and,
      // via the UNIQUE index on client_msg_id, stop the real card landing at
      // all.
      if (s.clientMsgId.startsWith(AGENT_COMMIT_PREFIX) || s.clientMsgId.startsWith(AGENT_TURN_PREFIX)) {
        throw new Error('bad_payload');
      }
      // The cap lives in shared/security.ts with every other entry limit, so
      // the composer's maxLength and this rejection read the same number
      // (card YYrJc5OkW7EP). 'too_long' is its own code, so the client can
      // say the message is too long instead of "bad payload".
      if (s.text.length === 0) throw new Error('bad_payload');
      if (s.text.length > MAX_THREAD_TEXT) throw new Error('too_long');
      // Invoke-path chip (decision 2026-10-02): optional and strictly boolean so
      // a malformed frame is refused rather than silently coerced to "ask".
      if (s.wantsAgent !== undefined && typeof s.wantsAgent !== 'boolean') {
        throw new Error('bad_payload');
      }
      break;
    }
    case 'msg-remove': {
      // Card — delete your own message. The seq must be a positive integer;
      // the DO enforces OWNERSHIP (you can only remove your own rows), so this
      // parser only checks shape, never the row's sender.
      const r = m as { seq?: unknown };
      if (typeof r.seq !== 'number' || !Number.isInteger(r.seq) || r.seq < 1) throw new Error('bad_payload');
      break;
    }
    case 'history': {
      const h = m as { before?: unknown; limit?: unknown; sinceDays?: unknown };
      if (h.before !== undefined && typeof h.before !== 'number') throw new Error('bad_payload');
      if (h.limit !== undefined && typeof h.limit !== 'number') throw new Error('bad_payload');
      if (h.sinceDays !== undefined && typeof h.sinceDays !== 'number') throw new Error('bad_payload');
      break;
    }
    case 'presence': {
      // Card J9hHjSZDFTCV — strictly boolean, so a malformed frame is refused
      // rather than silently coerced: `hidden: "false"` would otherwise read
      // as truthy and put a partner who is actually watching back offline.
      const p = m as { hidden?: unknown };
      if (typeof p.hidden !== 'boolean') throw new Error('bad_payload');
      break;
    }
    case 'ping':
      break;
    case 'typing':
      // Card tPPB3ozS4YrK — payload-free by design, so there is nothing to
      // validate: an extra field is ignored rather than refused, because a
      // client adding one must not lose its socket over a hint that costs
      // nothing to drop.
      break;
    default:
      throw new Error('bad_payload');
  }
  return obj as ClientMessage;
}

/**
 * Card J9hHjSZDFTCV — is the presence frame switched on for this worker?
 *
 * Fail-closed by construction: unset, empty, or any value outside the accepted
 * set means OFF, so a typo in the deploy can only ever remove the glow, never
 * switch a feature on by accident. The web client mirrors this through
 * /api/auth/config and requires `=== true`, so the flag is honoured on both
 * sides of the wire.
 */
export function presenceEnabled(env: { PEAR_PRESENCE?: string }): boolean {
  const v = (env.PEAR_PRESENCE ?? '').trim().toLowerCase();
  return v === '1' || v === 'on' || v === 'true';
}

/**
 * Card J9hHjSZDFTCV — how long a presence entry survives without a frame.
 *
 * The socket's own close event is the primary "gone" signal and the client's
 * `hidden` frame is the tab-hidden one; this TTL is the safety net for the
 * case neither event reaches the room (a device that falls off the network
 * with the socket half-open). Evaluated when a snapshot is built, never by a
 * timer, so an idle room costs nothing and no alarm is armed for it.
 */
export const PRESENCE_TTL_MS = 75_000;

/**
 * Card tPPB3ozS4YrK — is the typing indicator switched on for this worker?
 *
 * Same fail-closed contract as presenceEnabled: unset, empty or any value
 * outside the accepted set means OFF, so the worst a deploy typo can do is
 * remove the hint. Off removes BOTH halves — the room stops relaying and
 * /api/auth/config reports false, which is the only thing the client renders
 * from — so there is no state where frames fly and nobody is allowed to show
 * them, or the reverse.
 */
export function typingEnabled(env: { PEAR_TYPING?: string }): boolean {
  const v = (env.PEAR_TYPING ?? '').trim().toLowerCase();
  return v === '1' || v === 'on' || v === 'true';
}

/**
 * Card tPPB3ozS4YrK — the client's floor between two typing frames.
 *
 * One frame per two seconds is the whole rate contract: a partner's keystrokes
 * must not become a stream of socket writes, and the room enforces the same
 * window rather than trusting the client to be polite. A frame inside the
 * window is dropped silently — the sender is not wrong, just early, and an
 * error frame would close a socket over a hint.
 */
export const TYPING_MIN_INTERVAL_MS = 2_000;

/**
 * Card tPPB3ozS4YrK — how long a typing hint survives without a new one.
 *
 * Counted from the frame's ARRIVAL on the receiving device, never from the
 * sender's clock and never by a timer on the room: the room relays and forgets,
 * the receiver holds the hint for four seconds and drops it. That is the
 * "auto-expires after 4s of silence" clause, and it costs an idle room
 * nothing.
 */
export const TYPING_TTL_MS = 4_000;

/** History page size: default 50, hard cap 200. */
export const HISTORY_DEFAULT_LIMIT = 50;
export const HISTORY_MAX_LIMIT = 200;

/* Archive search (card 2sjo3uNvaRDr). The thread hydrates a 7-day window, so
   the browser never holds years of history — findability has to live on the
   server. One search is one bounded read of the couple's own rows: a cap so a
   paste cannot drag the whole text column into a frame, and a floor so a
   single character is not a scan. */
export const SEARCH_DEFAULT_LIMIT = 25;
export const SEARCH_MAX_LIMIT = 50;
export const SEARCH_MIN_QUERY = 2;

/* ------------------------------------------------------------------ *
 * 5. Durable Object signatures (one instance per couple)
 * ------------------------------------------------------------------ */

/**
 * The DO class (`CoupleRoom`, durable-object/couple-room.ts) MUST implement
 * this interface as RPC methods (wrangler RPC), plus a `fetch` handler that
 * accepts `GET /ws` with an `Upgrade: websocket` header and speaks §4.
 *
 * Ordering rule: every mutation runs inside ctx.storage.blockConcurrencyWhile
 * (or equivalent serialization) so `seq` is assigned once and broadcast in the
 * same order to every attached socket.
 *
 * Isolation rule: all rows live in this instance's own ctx.storage.sql. There
 * is no query that can name another couple. Cross-tenant probe (parent-run)
 * asserts couple A's methods never return couple B's rows.
 */
export interface CoupleRoomDO {
  /**
   * Partner A only. `code` is generated by the WORKER via `generateInviteCode()`
   * (the worker must own generation: it has to derive coupleId = f(code) BEFORE
   * it can address the DO). Fails `code_collision` if this couple already exists —
   * the worker retries with a fresh code, up to 5 times.
   */
  createCouple(code: string, displayName: string): Promise<JoinResult>;

  /** Partner B. Fails `expired_code` / `code_consumed` / `couple_full`. */
  join(code: string, displayName: string): Promise<JoinResult>;

  /**
   * Card j1pvawhlLBks — re-issue the invite window for an UNPAIRED couple.
   *
   * The invite dies INVITE_TTL_MS after create and nothing could extend it:
   * the partner holding an expired code got `expired_code`, the creator's
   * screen kept showing a code that no longer worked, and the only way forward
   * was to abandon the couple and create another one. This extends the SAME
   * code — it is the credential the partner may already hold, so minting a
   * fresh one would discard the invite in their hand. Fails `already_paired`
   * once the couple is complete (nobody left to invite) and `not_found` when
   * the room has no couple yet.
   */
  refreshInvite(now?: number): Promise<{ code: string; expiresAt: number }>;

  /**
   * Card tJdisOEkVwha — mint a single-use, 24-hour invite LINK.
   *
   * `link_id` is an unguessable nonce the WORKER signs into the emailed URL
   * (the DO never sees the URL and stores no secret beyond the id). The record
   * is the single-use ledger: redeemInviteLink() stamps `consumed_at` exactly
   * once, so a forwarded or re-opened link admits nobody a second time.
   *
   * Independent of the couple's own INVITE_TTL_MS window: the emailed link is
   * the credential for its whole life, which is the point — an invitation
   * opened the next morning must still work.
   */
  createInviteLink(input: { memberId: string; ttlMs?: number }): Promise<{ linkId: string; expiresAt: number }>;

  /** Card tJdisOEkVwha — is this emailed link still good, and for which code?
   *  Answers `ok:false` for unknown / spent / expired, so the signed-out join
   *  screen can name the couple before anyone has signed in. */
  inviteLinkState(linkId: string): Promise<{ ok: boolean; code: string | null; expiresAt: number | null; reason: 'link_invalid' | 'link_used' | 'link_expired' | null }>;

  /**
   * Card tJdisOEkVwha — join THROUGH an emailed link.
   *
   * Same join as the code path, with two differences the card is about:
   *   1. the credential is the link, so the couple's ten-minute code window
   *      does not apply — a valid link admits the partner whatever the code
   *      clock says;
   *   2. the link is spent as part of the join, inside the same serial block,
   *      so two devices racing the same URL cannot both get in.
   * The room's own rules still hold: a closed or already-full couple refuses.
   */
  joinWithLink(input: { linkId: string; displayName: string }): Promise<JoinResult>;

  /**
   * Card Vf23eSKKxDhX — the supported way OUT of a couple.
   *
   * Drops the member row, closes that member's attached socket and broadcasts
   * `members` so the survivor's screen re-reads the room. The room itself is
   * never deleted: a couple that falls to ONE member resets `paired_at` (so
   * the survivor can issue a fresh invite to someone else), and a couple that
   * falls to ZERO takes the `closed_at` tombstone — from then on join and
   * refreshInvite refuse it, so the old code cannot re-admit anyone.
   *
   * Returns who is left and whether the room closed. Fails `not_found` when
   * the member is not in this room (the caller's account row is stale).
   */
  removeMember(input: { memberId: string }): Promise<RemoveMemberResult>;

  /**
   * Card ocJz1TeLT4pi — one member's PRIVATE half of this room as an export
   * bundle. Reads only rows this member owns (shared=0 notebook, me-lane
   * inbox, my gift_private_lists / gift_preferences / member_interests /
   * member_profile birthday, my question answers, my sent requests). Nothing
   * shared, nothing the partner authored. Fails `not_found` when the member
   * is not in this room.
   */
  exportMemberBundle(memberId: string): Promise<ExitBundle>;

  /**
   * Card O2J0Cm1ZFeNd — re-file a previously exported bundle into THIS room
   * under `memberId`, regenerating id columns so a second import cannot
   * collide while preserving created_at / updated_at. Only the private half
   * imports; shared rows and couple-owned rows are dropped. Fails
   * `bad_bundle` on an unknown version or a malformed row, `not_found` when
   * the member is not in this room.
   */
  importMemberBundle(memberId: string, bundle: ExitBundle): Promise<{ imported: number }>;

  /** Second half of join — the ceremonial pairing moment (card 2NuQ8dd2JMqs):
   *  files the milestone in the Vault as a shared memory and broadcasts
   *  `paired` to every socket already attached. Idempotent; never called from
   *  inside join() (nested serial() deadlocks the instance). */
  completePairing(input: { memberId: string }): Promise<{ memoryItemId: string | null; sockets: number }>;

  /** Current couple or null. Used for status probes. */
  getCouple(): Promise<Couple | null>;

  /** Validate a member secret. Returns the member, or null. Used by /ws hello. */
  validateToken(memberId: string, secret: string): Promise<Member | null>;

  /**
   * Fresh couple token for an existing member (accounts epic, Phase C).
   * Reuses the member's stored secret — the token is the same one the
   * create/join path issued, re-minted because the client lost it (cleared
   * browser, new device). Fails `not_found` when the member row is gone.
   */
  mintToken(memberId: string): Promise<string>;

  /** Persist one message, assign seq/id/ts, broadcast to BOTH sockets.
   *  `roundId` marks a game-round card so the round can find its own card. */
  appendMessage(input: {
    kind: MessageKind;
    fromMemberId: string | null;
    text: string;
    clientMsgId?: string;
    roundId?: string;
  }): Promise<Message>;

  /** Rewrite a message's text in place, then broadcast msg-update to both. */
  updateMessage(seq: number, text: string): Promise<Message | null>;

  /**
   * Card jinkfGNSDI4j: keep Thread's ONE question-card in step with the
   * current question state — creates it at most once (scans existing
   * question-card messages for this questionId first), rewrites that same
   * row in place when state flips to 'revealed'. Idempotent: calling it
   * with unchanged state writes nothing. Never carries answer text while
   * pending.
   */
  syncQuestionCard(questionId: string): Promise<void>;

  /** Delete a message, then broadcast msg-remove to both. */
  removeMessage(seq: number): Promise<boolean>;

  /** Newest-first page. `before` = seq cursor (exclusive). Ascending order in return.
   *  `sinceDays` bounds the fresh-open read to the last N days (card 4ynbWszGdqUd). */
  history(opts?: { before?: number; limit?: number; sinceDays?: number }): Promise<{ messages: Message[]; hasMore: boolean }>;

  /** Archive search (card 2sjo3uNvaRDr): the whole thread, not the loaded
   *  window. Returns matching rows newest-first, capped at SEARCH_MAX_LIMIT.
   *  A query below SEARCH_MIN_QUERY returns [] rather than scanning. */
  searchMessages(query: string, limit?: number): Promise<Message[]>;

  /** Current head seq (0 when empty). */
  head(): Promise<number>;

  /** Pilot instrumentation (PRD §11, card M6uAXRqjPs7J). Fold this couple's
   *  answers / requests / inbox / AI usage into the anonymous cross-couple
   *  metrics tables. `activity` marks the current week as used; `metrics`
   *  only refreshes counters. Resolves null when D1 is unbound. */
  metricsTick(input?: { reason?: 'activity' | 'metrics' }): Promise<{ week: number; coupleKey: string } | null>;

  /** One model call's usage, priced once at the door (AI spend per couple). */
  recordAiUsage(input: { model: string; tokensIn: number; tokensOut: number }): Promise<{ usd: number; priced: 'table' | 'default' }>;

  /** Funnel event (cards 3oJPs7ZoVd89 / Yw5UFURMXSTY) — fire-and-forget,
   *  never throws, silent when POSTHOG_KEY is unset. The gift funnel fires
   *  these server-side so a client cannot over-count. */
  trackEvent(event: string, memberId: string | null, props?: Record<string, unknown>): void;

  /** The couple's LOCAL funnel log (card Qus8eYvFYfyO), newest first — every
   *  trackEvent row whether or not POSTHOG_KEY exists. Read back only through
   *  this couple's own session, and only for the member asking: their own rows
   *  plus unmarked system rows (card ggTQe2KbB0A3 HIGH-2). No viewerId, no
   *  rows — an unattributed read is the leak this replaced. */
  funnelEvents(limit?: number, viewerId?: string): Promise<FunnelEvent[]>;

  /** Dev-only: age a gift pick's acceptance so a probe can assert the T+5
   *  reaction gate without waiting five days. Routed only while PEAR_ENV
   *  is not prod. */
  devGiftFeedbackAge(recId: string, days: number): Promise<GiftRecommendation>;

  /** Dev-only: backdate the couple's clock, seed week-1 answers, record usage.
   *  Only reachable through POST /api/metrics/dev/seed behind PEAR_ENV=dev. */
  metricsDevSeed(input: {
    coupleCode?: string;
    backdateDays?: number;
    seedAnswersPerMember?: number;
    usage?: { model?: string; tokensIn?: number; tokensOut?: number };
  }): Promise<{ backdated: boolean; answersSeeded: number; usageUsd: number; currentWeek: number }>;
}

/* ------------------------------------------------------------------ *
 * 6. HTTP API surface (worker routes) — implemented by B (pairing) and D (chat)
 * ------------------------------------------------------------------ */

/** One row of the couple's own funnel log (card Qus8eYvFYfyO): every event
 *  trackEvent() saw, written whether or not POSTHOG_KEY exists, read back
 *  only through this couple's session. */
export interface FunnelEvent {
  seq: number;
  event: string;
  memberId: string;
  props: Record<string, unknown>;
  ts: number;
}

export interface ApiError {
  error: { code: ErrorCode; message: string };
}

// B owns pairing routes (worker/src/api/pairing.ts):
//   POST /api/pair/create   body {displayName}      -> JoinResult (role 'A')
//   POST /api/pair/join     body {code, displayName} -> JoinResult (role 'B')
//   GET  /api/pair/status   Auth: Bearer token       -> {couple: Couple, role}
// D owns chat routes (worker/src/api/chat.ts):
//   GET  /api/chat/history  Auth: Bearer token, ?before=&limit= -> {messages, hasMore}
//   POST /api/agent/card    Auth: Bearer token + X-Pear-Agent-Key -> {message}  (Phase 0 stub)
// Parent owns (worker/src/index.ts):
//   GET  /health            -> 200 {status:'ok', version}
//   GET  /ws                -> DO socket upgrade (token via ?token= or first frame 'hello')

/* ------------------------------------------------------------------ *
 * 7. SQLite schema (DO storage) — parent-authored, A implements
 * ------------------------------------------------------------------ */

export const COUPLE_SCHEMA_SQL = `
CREATE TABLE IF NOT EXISTS couple (
  code       TEXT PRIMARY KEY,
  created_at INTEGER NOT NULL,
  expires_at INTEGER NOT NULL,
  paired_at  INTEGER,
  -- Card Vf23eSKKxDhX: the tombstone. A couple whose LAST member left keeps
  -- its room (every memory, chore and message outlives the pairing) but stops
  -- opening its door: join/refreshInvite refuse a closed room, so the code
  -- that used to reach it can never re-admit anyone. Null = still open.
  closed_at  INTEGER
);
CREATE TABLE IF NOT EXISTS invite_links (
  link_id     TEXT PRIMARY KEY,
  created_at  INTEGER NOT NULL,
  expires_at  INTEGER NOT NULL,
  consumed_at INTEGER,
  created_by  TEXT
);
CREATE TABLE IF NOT EXISTS members (
  member_id    TEXT PRIMARY KEY,
  role         TEXT NOT NULL CHECK (role IN ('A','B')),
  display_name TEXT NOT NULL DEFAULT '',
  -- Reply-text colour key (card 9QADJv3CdiOh): one of REPLY_COLOUR_KEYS in
  -- shared/types.ts, NULL until picked. Rooms booted before this column
  -- existed get it from CoupleRoom's membersReplyColourMigration.
  reply_colour TEXT,
  joined_at    INTEGER NOT NULL,
  secret       TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS messages (
  seq            INTEGER PRIMARY KEY AUTOINCREMENT,
  id             TEXT NOT NULL,
  kind           TEXT NOT NULL CHECK (kind IN ('text','agent-card','kudos','request-card','question-card','date-card','system')),
  from_member_id TEXT,
  text           TEXT NOT NULL,
  ts             INTEGER NOT NULL,
  client_msg_id  TEXT UNIQUE,
  round_id       TEXT
);
-- Agent-work state, keyed by the SOURCE message (card OAcY41nByea9). The
-- message is persisted and acked BEFORE the agent turn starts, so a room
-- restart in that window used to make the replay look "already handled" and
-- the requested card was lost for ever. One row per message says what the
-- message asked for (wants_agent) and whether that work finished:
--   pending -> a replay, or the next welcome, RESUMES it
--   done    -> replay is a no-op (card iimHzKEyiFiw, unchanged)
-- commit_id is the clientMsgId the card will land under (the reservation id
-- when a turn was charged, else agent-card:<msgId>), so a resume can tell
-- "card already delivered" from "turn taken, card never written".
-- resv is the ConciergeTurnReservation JSON of a charged-but-unfinished turn,
-- refunded exactly once by BillingSlice.refundConciergeTurn before a retry.
-- Ids, flags and enums only — plaintext by the at-rest registry rule.
CREATE TABLE IF NOT EXISTS agent_work (
  source_msg_id TEXT PRIMARY KEY,
  wants_agent   INTEGER NOT NULL DEFAULT 0,
  state         TEXT NOT NULL DEFAULT 'pending',
  outcome       TEXT,
  commit_id     TEXT,
  resv          TEXT,
  updated_at    INTEGER NOT NULL
);
`;

/** Invariants the schema must enforce (asserted by the parent's probes): */
/* ================================================================== *
 * SPRINT 2 CONTRACT — requests, daily question, AI toggles.
 * Parent-authored (W0). Children implement slices AGAINST this and
 * never edit this file. DO exposes these as flattened RPC methods.
 * ================================================================== */

/* ---- 1. Requests: state machine + recurrence ---- */

/** Which view a request lives in (design v1.1 §6: "lane (life or us)").
 *  Rows written before the column existed store NULL and READ as 'life' —
 *  they already appear in Life today, so that is where they stay. */
export type RequestLane = 'life' | 'us';

export type RequestState =
  | 'posted'
  | 'countered'
  | 'accepted'
  | 'declined'
  | 'snoozed'
  | 'done';
export type RequestAction =
  | 'accept'
  | 'counter'
  | 'decline'
  | 'snooze'
  | 'resume'
  | 'done';

/** The closed set of states the row CHECK admits. The read path downgrades
 *  anything outside it to 'posted' rather than throwing (card DlK33bskwmPe:
 *  a stray row must never take a list down). */
export const REQUEST_STATES: readonly RequestState[] = [
  'posted',
  'countered',
  'accepted',
  'declined',
  'snoozed',
  'done',
];

/**
 * LEGALITY TABLE (card DlK33bskwmPe). Existing moves are unchanged; the new
 * ones are additive:
 *
 *   posted    -> accepted | countered | declined | snoozed
 *   countered -> accepted | declined | snoozed
 *   accepted  -> done | snoozed          (accepted is no longer terminal —
 *                                         'done' closes it; snoozing an
 *                                         accepted ask defers the work)
 *   snoozed   -> posted                  (resume/unsnooze — the return date
 *                                         has arrived, the ask is live again)
 *   declined  -> (terminal)
 *   done      -> (terminal)
 *
 * A counter still hands the decision back to the ORIGINAL requester
 * (accept/decline the counter); recurrence spawns a fresh 'posted' request
 * rather than reopening history. Snoozing keeps the row — old rows are
 * downgraded, never deleted.
 *
 * WHO MAY ACT: while `posted` only the PARTNER may act (you never accept your
 * own ask); while `countered` only the AUTHOR may act (the counter returned to
 * them); terminal states accept nobody. Snooze and done are bookkeeping, not
 * decisions: either partner may snooze or tick off. Violations ->
 * Error('illegal_transition').
 */
export const REQUEST_TRANSITIONS: Readonly<Record<RequestState, readonly RequestState[]>> = {
  posted: ['accepted', 'countered', 'declined', 'snoozed'],
  countered: ['accepted', 'declined', 'snoozed'],
  accepted: ['done', 'snoozed'],
  declined: [],
  snoozed: ['posted'],
  done: [],
};

/** 'resume' is canonical; the route also accepts 'unsnooze' as an alias for
 *  it — both land on 'posted'. */
export const REQUEST_ACTIONS: Readonly<Record<RequestAction, RequestState>> = {
  accept: 'accepted',
  counter: 'countered',
  decline: 'declined',
  snooze: 'snoozed',
  resume: 'posted',
  done: 'done',
};

export type RecurrenceFreq = 'daily' | 'weekly' | 'monthly';

export interface RecurrenceRule {
  freq: RecurrenceFreq;
  /** epoch ms of the next occurrence; updated each time one is spawned */
  nextDueAt: number;
}

export interface RequestItem {
  requestId: string;
  fromMemberId: string;
  toMemberId: string | null; // null = open to the couple
  text: string;
  state: RequestState;
  /** life = the Life view, us = the Us view. Pre-lane rows read as 'life'. */
  lane: RequestLane;
  /** Set only while state === 'snoozed': when the ask comes back. */
  snoozedUntil: number | null;
  counterText: string | null; // set when state === 'countered'
  recurrence: RecurrenceRule | null;
  createdAt: number;
  updatedAt: number;
  /** the request this one spawned (recurrence), if any */
  spawnedFromId: string | null;
  /** Members who gave kudos on this completed row — at most one entry each,
   *  and never the member who did the work (card HBYCnnD9q5FS). */
  kudosBy: string[];
  /** When the first kudos landed; null until someone gives one. */
  kudosAt: number | null;
  /** The member whose accept completed the row — that member may not kudos
   *  their own work. Set the moment the row reaches 'accepted'. */
  completedBy: string | null;
  /** Plan session that spawned the ask (card 7aTCRQ9j8PWV); null otherwise. */
  sourceEventId: string | null;
  /** Why a row reached 'done' without being accepted — only set when a
   *  departing member's inbound open requests are closed on leave (card
   *  uTudCJtABptq). 'closed_by_exit' is the only value today; a 'done' row
   *  with a null reason is a normal completion. */
  closedReason: string | null;
}

/* ---- Chores (card LhzRSTrWTY9w) ---- */

/** 'once' fires on a single date and closes; 'recurring' rolls its due date
 *  forward on every completion so the row returns on the next occurrence. */
export type ChoreCadence = 'once' | 'recurring';
export type ChoreFreq = RecurrenceRule['freq'];

/** How a recurring chore finds its next date (card VmoORelj1HuO).
 *  'fixed'      — the classic rule: daily / weekly / monthly from the due date.
 *  'interval'   — every N days, counted from the schedule, so a late tick
 *                 catches up to the next future slot instead of landing overdue.
 *  'completion' — N days after the tick itself: the rhythm follows the work,
 *                 which is what "every time I've done it, again in 3 days"
 *                 means in a household. */
export type ChoreRecurMode = 'fixed' | 'interval' | 'completion';

/** How a chore decides who is on it (card UeOwZSHfdZCh).
 *  'fixed'     — the row carries its assignee and only a reassignment moves
 *                it: the default, and the only rule chores written before
 *                this card could have had.
 *  'claim'     — nobody until someone takes it. Open at creation whatever the
 *                request sent, claimed by the first tapper, released back to
 *                open by the completion that ends the round.
 *  'alternate' — the pointer walks the couple on completions alone: the
 *                finisher passes the torch to the member who did not finish.
 *                No date, no weekday, no calendar anywhere in the rule. */
export type ChoreAssignMode = 'fixed' | 'claim' | 'alternate';

/** Valid modes, for the two doors that validate a request (DO + worker). */
export const CHORE_ASSIGN_MODES: ChoreAssignMode[] = ['fixed', 'claim', 'alternate'];

/** One day in ms — the unit both non-classic modes count in. */
export const CHORE_DAY = 86_400_000;

/** Sanity ceiling for an interval/completion step (a year), so a fat-fingered
 *  "every 9000 days" is a bad_payload rather than a chore nobody sees again. */
export const CHORE_MAX_EVERY_DAYS = 365;

/** How many times one occurrence may be pushed out before the couple is asked
 *  to change something instead (card xvom08w4BgGT: snooze max 3 per window,
 *  then offer reassign / renegotiate). Snooze and defer both consume it —
 *  they are the same "not this round" gesture with different destinations —
 *  and a completion resets it, because that ends the occurrence. */
export const CHORE_SNOOZE_CAP = 3;

/** What the options card offers when the cap is hit. The client renders
 *  these as verbs; the worker never assumes which one it will get. */
export type ChoreCapOption = 'reassign' | 'renegotiate';

/** Allow-listed reaction glyphs for a chore card (card MnorjNDwB3of). A
 *  closed set — not free text — is what makes comment threads impossible by
 *  design: there is nothing to type, so there is nothing to reply to. */
export const CHORE_REACTION_GLYPHS = ['👏', '❤️', '🙌', '🔥', '😮', '🎉'] as const;
export type ChoreReactionGlyph = (typeof CHORE_REACTION_GLYPHS)[number];

/** Max length of a done-note — a warm receipt line, not a message (card
 *  MnorjNDwB3of). Beyond this the worker answers too_long, matching the
 *  composer's maxLength so the two never disagree. */
export const CHORE_DONE_NOTE_MAX = 140;

/** One member's public reaction on a chore card. At most one per member — a
 *  new glyph replaces theirs, so a card never grows a comment pile. */
export interface ChoreReaction {
  memberId: string;
  glyph: string;
  at: number;
}

export interface ChoreItem {
  choreId: string;
  title: string;
  cadence: ChoreCadence;
  /** Only set when cadence === 'recurring'. */
  freq: ChoreFreq | null;
  /** Which rule drives the roll-forward. Rows written before this column
   *  existed read NULL, which means 'fixed' — the only rule they could have
   *  had. Always 'fixed' while cadence === 'once'. */
  recurMode: ChoreRecurMode;
  /** Step in days for 'interval' and 'completion'; null for 'fixed'. */
  recurEveryDays: number | null;
  /** Member the chore is assigned to; null = anyone's (or, in 'claim' mode,
   *  not yet taken). For 'alternate' it is the pointer: who goes next. */
  assigneeId: string | null;
  /** Which rule governs the assignee (card UeOwZSHfdZCh). A row written
   *  before the column existed reads NULL, which means 'fixed'. */
  assignMode: ChoreAssignMode;
  /** One-off due date, or the recurring row's NEXT occurrence. */
  dueAt: number | null;
  status: 'open' | 'done';
  completedAt: number | null;
  completedBy: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** Chosen at creation (card eudAJs92EZ9B): false means this chore stays in
   *  Pear and is never pushed to Google Calendar / Tasks. There is no edit
   *  path for it — the toggle is part of the create form, so a row that went
   *  out keeps going out and one that stayed in stays in. */
  syncGoogle: boolean;
  /** True when the create form's "Specify time" field was filled: dueAt then
   *  carries a real clock time (a time-block) instead of the local-NOON
   *  date-only sentinel. The sentinel can only be recognised in the client's
   *  own timezone, so the worker never infers it from the timestamp — the
   *  client states it (card ZgLp6PjCXHEm). NULL in the row = written before
   *  the column existed = date-only, which is how those rows always synced. */
  hasTime: boolean;
  /** Times THIS occurrence has been pushed out (snooze or defer). Resets to 0
   *  when the chore completes, which ends the occurrence (card xvom08w4BgGT).
   *  NULL column on a row written before the field existed reads 0. */
  snoozeCount: number;
  /** Public reactions on this card (card MnorjNDwB3of) — at most one entry
   *  per member, so a couple's two taps are the whole thread. Empty when
   *  nobody has reacted. */
  reactions: ChoreReaction[];
  /** The warm receipt left when this occurrence was ticked off — "done — out
   *  of bin liners" (card MnorjNDwB3of). NULL when the completer skipped the
   *  note. For a 'once' row it lives on the closed card; for a recurring row
   *  it is cleared with the round, so the next tick starts note-free. */
  doneNote: string | null;
  /** Who left the done-note — usually the completer, but the route lets the
   *  partner add one to a card they did not finish. NULL with doneNote. */
  doneNoteBy: string | null;
}

/** Step per cadence unit, in ms. Monthly is the same approximation the
 *  request recurrence uses, so one date rolls forward identically everywhere. */
export const CHORE_FREQ_STEPS: Record<ChoreFreq, number> = {
  daily: 86_400_000,
  weekly: 604_800_000,
  monthly: 2_592_000_000,
};

/** Next due date after a completion: the step lands on the CURRENT due date
 *  when one is set, else on the completion clock — an undated recurring chore
 *  starts its cycle at the first tick rather than being born overdue. */
export function nextChoreDueAt(dueAt: number | null, freq: ChoreFreq, now: number): number {
  return (dueAt ?? now) + CHORE_FREQ_STEPS[freq];
}

/** Next due date for a row under whichever rule it carries (card VmoORelj1HuO).
 *  'fixed' keeps the arithmetic it has always had — the probe pins it — so
 *  only the two new modes get their own branch. */
export function nextChoreDueFor(
  chore: {
    dueAt: number | null;
    freq: ChoreFreq | null;
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
  },
  now: number,
): number {
  const mode = chore.recurMode ?? 'fixed';
  if (mode === 'completion') {
    const step = Math.max(1, Math.round(chore.recurEveryDays ?? 1)) * CHORE_DAY;
    return now + step;
  }
  if (mode === 'interval') {
    const step = Math.max(1, Math.round(chore.recurEveryDays ?? 1)) * CHORE_DAY;
    // Counted from the schedule, not from the tick: finishing on day 5 of a
    // 3-day rhythm lands the next one on day 6, and stepping forward from a
    // long-neglected date skips the past rather than booking an overdue row.
    let next = (chore.dueAt ?? now) + step;
    while (next <= now) next += step;
    return next;
  }
  return nextChoreDueAt(chore.dueAt, chore.freq ?? 'weekly', now);
}

/** The couple's list-clear streak (card xvom08w4BgGT). Couple-scoped by
 *  construction — one row per Durable Object, never attributed to a person —
 *  and counted in ISO weeks: a week whose list goes clear counts once, and a
 *  week that passes without a clear run resets the streak to 1 at the next
 *  clear rather than to 0, because the celebration is the clear itself. */
export interface ChoreStreak {
  /** Consecutive weeks the whole list has gone clear. */
  streak: number;
  /** ISO week key (YYYYWW) of the last clear, or null if it never happened. */
  lastClearWeek: number | null;
  /** When that clear fired — the client celebrates once per event, not once
   *  per visit, by remembering the last value it has already shown. */
  lastClearAt: number | null;
}

/** What the fairness engine MAY tell a client (card S1YSeaemeA2N): whether it
 *  wants to speak and on what — never the counts. The per-member reliability
 *  numbers are stripped inside the DO, because a scoreboard between the two
 *  people using the product is ammunition (spec kZX3gsJkKaTH, constraints). */
export interface FairnessPublicState {
  /** Enough history (21 days) and every member past the occurrence floor. */
  eligible: boolean;
  fires: boolean;
  reason: 'lag' | 'reliability' | null;
  /** Why it stayed quiet — history / minimum / alternating / consistent. */
  quiet: 'history' | 'minimum' | 'alternating' | 'consistent' | 'no-chore' | null;
  choreId: string | null;
  laggingMemberId: string | null;
  /** Slice C: the card currently on the rail, or null. Both partners read the
   *  SAME card — there is no private version of the question. */
  card: FairnessCardPublic | null;
}

/** The drift card itself (spec §3): one chore, one move, one pair of answers.
 *  Carries no counts and no reason string — the same scoreboard rule as the
 *  state above, because the card is what a partner actually sees. */
export interface FairnessCardPublic {
  choreId: string;
  /** For the button's own words ("Move 'Vacuum' to Maya"). */
  choreTitle: string;
  /** Whose name the chore currently carries, and who it would move to. */
  fromMemberId: string | null;
  toMemberId: string;
  createdAt: number;
  /** The card self-expires: after this it is gone whether or not anyone read
   *  it, so a question can never sit in the rail for a month. */
  expiresAt: number;
}

/** What the weekly pass reports back (spec §3) — `reason` is why it did NOT
 *  offer a card, and is null exactly when a card was created. Probe-only in
 *  practice: the rail reads the state, not this. */
export interface FairnessPassResult {
  card: FairnessCardPublic | null;
  reason: string | null;
}

/** Dev-only: the full evaluation plus the rows behind it, so the probe can
 *  assert the thresholds rather than infer them. Served only when
 *  PEAR_ENV != prod (worker/src/api/chores.ts). */
export interface FairnessDebugState {
  evaluation: FairnessEvaluation;
  ledger: { choreId: string; memberId: string; completedAt: number; dueAt: number | null; wasLate: boolean }[];
  lag: { choreId: string; firstMissedAt: number; misses: number; assigneeId: string | null; assignMode: string }[];
  /** Ledger rows the backfill just rebuilt from the chores' own doer stamps. */
  backfilled: number;
}

export interface ChoresAPI {
  createChore(input: {
    createdBy: string;
    title: string;
    cadence: ChoreCadence;
    freq?: ChoreFreq | null;
    /** Which roll-forward rule: default 'fixed'. 'interval' and 'completion'
     *  carry recurEveryDays (1–365) instead of a freq. */
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
    /** Which rule owns the assignee (card UeOwZSHfdZCh): default 'fixed'.
     *  'claim' refuses a seeded assignee — a claim chore starts open. */
    assignMode?: ChoreAssignMode | null;
    assigneeId?: string | null;
    dueAt?: number | null;
    /** Default true: the "Add to Calendar & Tasks" switch on the create form
     *  (false keeps the chore out of the Google outbox entirely). */
    syncGoogle?: boolean;
    /** True when the "Specify time" field was filled — dueAt then carries a
     *  clock time instead of the local-NOON date-only sentinel. */
    hasTime?: boolean;
  }): Promise<ChoreItem>;
  /** Open work first, then dated, then done — the order the Life screen draws. */
  listChores(): Promise<ChoreItem[]>;
  /** assigneeId null clears the assignment. An id outside this couple is
   *  Error('bad_payload'); an unknown chore is Error('not_found'). A 'claim'
   *  chore is the exception: it can only be released (null) or claimed by
   *  the caller — handing it to someone else is Error('bad_payload'). */
  assignChore(choreId: string, assigneeId: string | null, actorId?: string | null): Promise<ChoreItem>;
  /** Take an open 'claim' chore (card UeOwZSHfdZCh). Claiming your own is a
   *  no-op, a chore the partner already holds is Error('already_claimed'),
   *  one that is not in 'claim' mode is Error('bad_payload'). */
  claimChore(choreId: string, memberId: string): Promise<ChoreItem>;
  /** 'once' -> status 'done'. 'recurring' -> completed_by stamped and dueAt
   *  rolled forward, status stays 'open'. A finished row is
   *  Error('illegal_transition'). Two rules ride along with the tick: an
   *  'alternate' row whose tick left it open hands the pointer to the member
   *  who did NOT finish, and a 'claim' row whose tick left it open goes back
   *  to unclaimed so the next round can be taken.
   *
   *  The optional note (card MnorjNDwB3of) is the warm receipt — "done — out
   *  of bin liners". Trimmed, capped at CHORE_DONE_NOTE_MAX (over →
   *  Error('too_long')), stored with the member who ticked. A recurring row
   *  clears it with the round; a 'once' row keeps it on the closed card. */
  completeChore(choreId: string, memberId: string, note?: string | null): Promise<ChoreItem>;
  /** Set the caller's public reaction on a chore (card MnorjNDwB3of). The
   *  glyph is one of CHORE_REACTION_GLYPHS (else Error('bad_payload')), one
   *  per member — a second tap with a different glyph replaces theirs, the
   *  same glyph clears it (a toggle). Any member may react to any row, open
   *  or done. An unknown chore is Error('not_found'). No threads by design:
   *  a reaction is a glyph, never a reply. */
  reactChore(choreId: string, memberId: string, glyph: string): Promise<ChoreItem>;
  /** Add or replace a done-note on a row the caller did NOT tick off (card
   *  MnorjNDwB3of) — the partner can still leave a receipt. Empty string
   *  clears it. Over the cap → Error('too_long'); a row that has never been
   *  completed is Error('illegal_transition') — a receipt needs a completion
   *  to receipt. Either member may write it; doneNoteBy records who. */
  setChoreDoneNote(choreId: string, memberId: string, note: string): Promise<ChoreItem>;
  /** Push THIS occurrence to a chosen return time (card xvom08w4BgGT). The
   *  first three pushes in an occurrence land; the fourth is Error('snooze_cap')
   *  — the couple has already moved it three times, so the answer is a change
   *  of plan (reassign / renegotiate), not a fourth date. An unknown chore is
   *  Error('not_found'), a finished one Error('illegal_transition'), a return
   *  time in the past Error('bad_payload'). */
  snoozeChore(choreId: string, memberId: string, returnAt: number): Promise<ChoreItem>;
  /** Move the occurrence to its next natural slot without choosing a date
   *  (same cap, same reset rules as snooze): a recurring row rolls under its
   *  own recurrence rule, a 'once' row is pushed a day. */
  deferChore(choreId: string, memberId: string): Promise<ChoreItem>;
  /** The couple's list-clear streak — read-only, and never keyed to a member. */
  choreStreak(): Promise<ChoreStreak>;
  /** Dev-only probe seam: place the last clear in a past week so the streak's
   *  gap-reset can be tested without waiting a week out. The worker route
   *  refuses it when PEAR_ENV=prod (card xvom08w4BgGT). */
  devStreakSet(lastClearWeek: number, streak: number, now: number): Promise<ChoreStreak>;
  /** Fairness engine, user-facing half (card S1YSeaemeA2N, spec
   *  kZX3gsJkKaTH): evaluate the ledger + lag and say whether the engine
   *  wants to speak. Runs the backfill and the lag sweep on the way through,
   *  and carries no per-member numbers. */
  fairnessState(): Promise<FairnessPublicState>;
  /** Slice C (spec §3): the weekly pass. Creates at most one card, only when
   *  the rule layer fires and both caps allow it. Returns the card, or why
   *  there is none. Never throws. */
  runFairnessCardPass(now: number): Promise<FairnessPassResult>;
  /** Accept the live card: the chore flips to 'alternate' with the pointer on
   *  the taker and the books are archived. No live card is
   *  Error('illegal_transition'). */
  acceptFairnessCard(memberId: string, now: number): Promise<FairnessCardPublic>;
  /** "Not now": the card closes for good (its 45-day quiet was stamped at
   *  offer time, so this chore cannot come back before then). */
  dismissFairnessCard(memberId: string, now: number): Promise<FairnessCardPublic>;
  /** Dev-only probe seam: run the pass with an injected clock, so the caps and
   *  the TTL are testable without waiting them out. Route refuses it at prod. */
  devFairnessPass(now: number): Promise<FairnessPassResult>;
  /** Dev-only probe seam: backdate ledger rows (and optionally prune them so
   *  the backfill has a pre-ledger couple), then return the full evaluation.
   *  The worker route refuses it when PEAR_ENV=prod. */
  devFairness(input: {
    pruneLedger?: boolean;
    entries?: { choreId?: unknown; memberId?: unknown; completedAt?: unknown; dueAt?: unknown; wasLate?: unknown }[];
  }, now: number): Promise<FairnessDebugState>;
  /** Edit what the row carries (card VmoORelj1HuO): title, cadence and its
   *  recurrence, due date, assignee. Omitted fields are left alone; a bad
   *  value is Error('bad_payload'), an unknown chore Error('not_found'). */
  updateChore(choreId: string, patch: {
    title?: string;
    cadence?: ChoreCadence;
    freq?: ChoreFreq | null;
    recurMode?: ChoreRecurMode | null;
    recurEveryDays?: number | null;
    dueAt?: number | null;
    hasTime?: boolean;
    assigneeId?: string | null;
    /** Switching a chore INTO 'claim' clears whoever held it — the mode
     *  starts open, so an edit can never leave a claim already filled. */
    assignMode?: ChoreAssignMode | null;
  }): Promise<ChoreItem>;
  /** Remove the row. An unknown chore is Error('not_found'). */
  deleteChore(choreId: string): Promise<void>;
}

/* ---- Chores → Google Calendar + Tasks connect (card hWnpJn7Tfl38) ---- */

/** What the Chores panel is allowed to see. Tokens never leave the DO. */
export interface GoogleConnectStatus {
  provider: string;
  accountEmail: string | null;
  scopes: string[];
  connectedAt: number | null;
}

export interface GoogleConnectAPI {
  /** null when the couple has never connected. */
  getGoogleConnect(): Promise<GoogleConnectStatus | null>;
  /** Upsert the single row (id = 1). A repeat consent that returns no new
   *  refresh token keeps the one already stored — Google withholds it on
   *  re-consent, and losing it would silently break every later write. */
  setGoogleConnect(input: {
    provider: string;
    accountEmail: string | null;
    scopes: string[];
    accessToken: string;
    refreshToken: string | null;
    expiresAt: number | null;
    connectedBy: string | null;
  }): Promise<GoogleConnectStatus>;
  clearGoogleConnect(): Promise<void>;
}

/* ---- Chores → Google Calendar + Tasks writes (card FuQ5MHiHZo7z) ---- */

/**
 * One queued push. Deliberately holds NO couple content: the flush reads the
 * chore row itself (already encrypted at rest through chores.title) instead of
 * copying a title into a second table that would then need its own registry
 * entry. Only ids, enums and a short error code live in the queue.
 */
export interface GoogleSyncItem {
  choreId: string;
  /** 'upsert' keeps Google current (create or patch); 'complete' closes a
   *  'once' task. A recurring completion rolls its date forward, so it
   *  enqueues as 'upsert' — the row is not done, it moved. */
  op: 'upsert' | 'complete';
  attempts: number;
  /** 'task' = Google Tasks (cadence 'once'); 'event' = Calendar (recurring).
   *  Once a link exists it wins: the kind can never flip under a live id. */
  kind: 'task' | 'event';
  /** Google's id for this chore, null until the first push succeeds. */
  externalId: string | null;
  assigneeName: string | null;
  chore: ChoreItem;
}

export interface GoogleSyncStats {
  /** Queued rows still under the retry ceiling. */
  pending: number;
  /** Rows that hit the ceiling and are parked until something re-enqueues them. */
  failed: number;
  /** Chores that have a Google id of their own. */
  linked: number;
}

export interface GoogleSyncAPI {
  googleSyncStats(): Promise<GoogleSyncStats>;
  /** Queued work, oldest first, only while the couple is connected. Each item
   *  carries the chore row as it stands NOW — not as it stood when queued. */
  googleSyncBatch(limit?: number): Promise<GoogleSyncItem[]>;
  googleSyncAck(choreId: string, kind: 'task' | 'event', externalId: string): Promise<void>;
  /** Completed a one-off that lived on Calendar: Google's object is gone, so
   *  both the queued row and the link are dropped (card ZgLp6PjCXHEm). */
  googleSyncDrop(choreId: string): Promise<void>;
  googleSyncFail(choreId: string, error: string): Promise<void>;
  /** Drop every queued push (disconnect). The Google links stay: reconnecting
   *  the same account resumes the same objects instead of duplicating them. */
  resetGoogleSync(): Promise<void>;
  /** Tokens for a write about to talk to Google — never part of the browser
   *  surface (GoogleConnectAPI), worker code only. */
  googleTokens(): Promise<{ accessToken: string; refreshToken: string | null; expiresAt: number | null } | null>;
  /** Store a refreshed access token; the refresh token is untouched. */
  updateGoogleAccessToken(accessToken: string, expiresAt: number | null): Promise<void>;
}

export interface RequestsAPI {
  createRequest(input: { fromMemberId: string; text: string; recurrence?: RecurrenceRule | null; lane?: RequestLane }): Promise<RequestItem>;
  listRequests(filter?: { state?: RequestState; lane?: RequestLane }): Promise<RequestItem[]>;
  /** Illegal moves throw Error('illegal_transition'); unknown id -> Error('not_found').
   *  The 4th argument accepts the legacy `counterText` string OR an options
   *  object — `returnAt` rides the object for the 'snooze' action, because the
   *  CoupleRoom RPC wrapper forwards exactly four arguments and this card does
   *  not change it. A missing/invalid returnAt -> Error('bad_payload'). */
  transitionRequest(
    requestId: string,
    memberId: string,
    action: RequestAction,
    opts?: string | { counterText?: string; returnAt?: number },
  ): Promise<RequestItem>;
  /** Test-clock scan: spawn one occurrence per due recurring request. Returns spawned items. */
  tickRequests(now?: number): Promise<RequestItem[]>;
  /** Kudos on a COMPLETED request (state 'accepted'). Throws
   *  Error('illegal_transition') when the row is not completed,
   *  Error('self_kudos') for the member who did the work, and
   *  Error('already_given') when that member has already given one. */
  giveRequestKudos(requestId: string, memberId: string): Promise<RequestItem>;
}

/* ---- 1b. Game: a back-and-forth topic round (card 5IXdtz7BL3CX) ---- */

/* Would You Rather modes (card DbcAi0izvJln, founder email 2026-10-19): a
 * structured A-or-B game kind beside the free-text topic round — three
 * curated banks, each its own category so keep/discard and the rotation
 * treat a mode the way they treat any other game. */
export type WyrMode = 'wyr-silly' | 'wyr-spicy' | 'wyr-deep';
export const WYR_MODES: WyrMode[] = ['wyr-silly', 'wyr-spicy', 'wyr-deep'];
export function isWyrMode(v: unknown): v is WyrMode {
  return typeof v === 'string' && (WYR_MODES as string[]).includes(v);
}

/** Closed category list — the concierge picks from these, never free-form. */
export type GameCategory = 'places' | 'restaurants' | 'would-you-rather' | 'this-or-that' | 'memory' | WyrMode;

/** Post-round verdict on a game (card OJmoL0w1fGrm). Couple-level per
 * category: a 'discard' drops the category out of the random rotation. */
export type GameRatingVerdict = 'keep' | 'discard';
export type GameRatings = Partial<Record<GameCategory, GameRatingVerdict>>;

export interface GameRound {
  roundId: string;
  category: GameCategory;
  question: string;
  /** 'closed' = abandoned by "next topic" before both answered. */
  state: 'open' | 'revealed' | 'closed';
  createdAt: number;
  revealedAt: number | null;
  /** Who has answered or passed — the text stays hidden until reveal. */
  answeredBy: string[];
  /** This member's own answer, always visible to them. */
  myAnswer: string | null;
  myPassed: boolean;
  /** The partner's answer — null until the round is revealed. */
  partnerAnswer: string | null;
  partnerPassed: boolean;
  /** Couple's post-round verdict on this category, once given (card OJmoL0w1fGrm). */
  rating: GameRatingVerdict | null;
  /** Would-You-Rather options — exactly two strings, null for a topic round
   *  (card DbcAi0izvJln). The answer stored is the option the member picked,
   *  so the reveal and the thread card read as plain text either way. */
  options: [string, string] | null;
  /** Turns completed in the current session (card cNGAFvkiRCGz): a turn is
   *  ONE revealed round; the panel shows the closing card at the limit. */
  turns: number;
}

/** Session length for a game run (card cNGAFvkiRCGz) — 5 revealed rounds,
 *  then the closing card. The counter resets when a fresh round opens. */
export const GAME_TURN_LIMIT = 5;

export interface GameAPI {
  /** The open round, or null when nothing is in play. */
  getGameRound(memberId: string): Promise<GameRound | null>;
  /** Issue a topic. `advance` closes the current round first, so "next topic"
   *  and "start" are one call. An open round is returned unchanged unless
   *  advance is set — both partners must see the same prompt. */
  startGameRound(memberId: string, category?: GameCategory, advance?: boolean): Promise<GameRound>;
  answerGameRound(memberId: string, text: string): Promise<GameRound>;
  passGameRound(memberId: string): Promise<GameRound>;
  /** Record a couple-level verdict on the round's category. Only a revealed
   *  round can be rated; the verdict persists and steers the rotation. */
  rateGameRound(memberId: string, roundId: string, verdict: GameRatingVerdict): Promise<GameRound>;
  /** Every category verdict the couple has given. */
  getGameRatings(): Promise<GameRatings>;
}

/* ---- Guess Me: the two-player round engine (card eq92fFXqQUuP) ---------- */

/** Question families. Curated banks live in the Durable Object — a closed
 *  list like every other game surface, never model output at round-open.
 *  'spicy' is opt-in: it is only ever opened by an explicit pick, never
 *  rotation, never a recommendation. */
export type GuessMeCategory = 'small-stuff' | 'past' | 'dreams' | 'stress-needs' | 'spicy';

export const GUESS_ME_CATEGORIES: GuessMeCategory[] = [
  'small-stuff', 'past', 'dreams', 'stress-needs', 'spicy',
];

export const GUESS_ME_CATEGORY_LABELS: Record<GuessMeCategory, string> = {
  'small-stuff': 'The small stuff',
  past: 'Our past',
  dreams: 'Dreams',
  'stress-needs': 'Stress and needs',
  spicy: 'Spicy',
};

/** The round state machine (card eq92fFXqQUuP): open → A_locked →
 *  B_locked → revealed. A locked submission is immutable; reveal fires
 *  exactly once and every later call reads the stored verdict. */
export type GuessMeState = 'open' | 'A_locked' | 'B_locked' | 'revealed';

/** exact = 2 points, close = 1, miss = 0. */
export type GuessMeVerdict = 'exact' | 'close' | 'miss';

/** Where the verdict came from — 'match' is a mechanical option compare
 *  (the guesser picked one of the three shipped choices), 'ai' is the
 *  free-text judge, 'fallback' is the literal compare that stands in when the
 *  judge is unavailable or unusable (correctable, like 'ai'), 'override' is a
 *  partner overruling it. */
export type GuessMeVerdictSource = 'match' | 'ai' | 'fallback' | 'override';

export const GUESS_ME_SCORE: Record<GuessMeVerdict, number> = {
  exact: 2, close: 1, miss: 0,
};

/** Questions per couple per UTC day (card eq92fFXqQUuP). */
export const GUESS_ME_DAILY_CAP = 3;

export interface GuessMeRound {
  roundId: string;
  /** 1-based; roles swap on parity — odd rounds A answers, even rounds B. */
  index: number;
  category: GuessMeCategory;
  question: string;
  state: GuessMeState;
  createdAt: number;
  revealedAt: number | null;
  /** The CALLING member's role this round. */
  myRole: 'answerer' | 'guesser';
  /** The caller's own submission, always theirs to see. */
  myAnswer: string | null;
  myGuess: string | null;
  /** True once locked — the UI stops offering an edit. */
  myLocked: boolean;
  /** The real answer. Null for the guesser until reveal, always. */
  partnerAnswer: string | null;
  /** The guess the partner made — only shown to the answerer at reveal. */
  partnerGuess: string | null;
  /** The guesser's three choices (real answer + two decoys), shuffled
   *  server-side. Null for the answerer, and null for the guesser until
   *  the decoys are generated. Never identifies which one is real. */
  options: string[] | null;
  verdict: GuessMeVerdict | null;
  verdictSource: GuessMeVerdictSource | null;
  /** True once the answer has actually reached the notebook — the card must
   *  not claim filing it did not do (sol review 2026-10-19). */
  filed: boolean;
  score: number;
  /** The couple's cumulative knowing score in this category. */
  categoryScore: number;
  /** Rounds the couple may still open today. */
  todayLeft: number;
}

export interface GuessMeAPI {
  /** Latest round for this couple, or null.
   *
   *  `now` is a clock seam for the browser walk that proves the daily
   *  allowance returns without a navigation (card qIRcApgTepJd). The worker
   *  forwards it only when PEAR_ENV !== 'prod', and it reaches the READ path
   *  only — every write still judges the cap against the real clock. */
  getGuessMeRound(memberId: string, now?: number): Promise<GuessMeRound | null>;
  /** One named round, if the caller is one of its two players — the read a
   *  retry needs, because "latest" has moved on by then. */
  getGuessMeRoundById(memberId: string, roundId: string): Promise<GuessMeRound | null>;
  /** Newest-first history for the list hook — unrevealed rounds carry only
   *  the caller's own side, exactly as the single-round view does. */
  getGuessMeHistory(memberId: string, limit?: number): Promise<GuessMeRound[]>;
  /** Open a round in a category. Throws 'cap_reached' at the daily cap. */
  startGuessMeRound(memberId: string, category: GuessMeCategory): Promise<GuessMeRound>;
  /** The answerer locks their answer. Immutable from here (card rule).
   *
   *  Every mutation names the round it belongs to. A caller goes stale the
   *  moment the couple moves on, and without the id a delayed answer, decoy
   *  write, guess or overrule lands on whatever round happens to be newest
   *  (sol review finding 1, 2026-10-19). An unknown id throws 'not_found'. */
  lockGuessMeAnswer(memberId: string, roundId: string, text: string): Promise<GuessMeRound>;
  /** Store the two decoys the worker's model produced for the locked
   *  answer. Answerer only, once, before any guess lands. */
  setGuessMeDecoys(memberId: string, roundId: string, decoys: [string, string]): Promise<GuessMeRound>;
  /** The guesser locks a pick — one of the options, or free text. */
  lockGuessMeGuess(memberId: string, roundId: string, text: string): Promise<GuessMeRound>;
  /** Apply a verdict. First call reveals and scores; later calls read back. */
  scoreGuessMeRound(
    memberId: string,
    roundId: string,
    verdict: GuessMeVerdict,
    source: GuessMeVerdictSource,
  ): Promise<GuessMeRound>;
  /** Cumulative knowing score per category. */
  getGuessMeScores(): Promise<Partial<Record<GuessMeCategory, number>>>;
  /** SERVER-INTERNAL. The question and the real answer behind the round that
   *  is waiting on a verdict — the one thing the guesser's own view must
   *  never carry, handed to the worker so it can judge a free-text guess (or
   *  settle an option pick) without the answer ever reaching a client. */
  getGuessMeJudgeContext(roundId: string): Promise<{ question: string; answer: string } | null>;
  /** SERVER-INTERNAL. The single-flight claim on the paid judge, and the
   *  recovery path for a round stranded in `B_locked` (sol review 2026-10-19).
   *  Exactly one caller is told to judge; a claimer that dies releases the
   *  claim when the lease expires and the next request judges the stored
   *  guess. Both sides are already locked, so the outcome is fully determined
   *  by stored data and the answer never has to reach a client. */
  claimGuessMeSettle(memberId: string, roundId: string): Promise<GuessMeSettlement>;
  /** SERVER-INTERNAL, idempotent. File every revealed round that has not
   *  reached the notebook yet — the durable retry behind "a note is written
   *  once, or it is written later". Runs from the room's daily sweep and from
   *  a guess POST that finds its round already settled; returns how many
   *  rounds it filed. */
  retryGuessMeFiling(): Promise<number>;
}

/** What a caller learns when it asks to settle a round. `claimed` is the only
 *  state that may call the model; `busy` means another request holds the
 *  lease; `settled` means the reveal already landed; `none` means this round
 *  is not waiting on a verdict, or is not this member's to settle. */
export interface GuessMeSettlement {
  state: 'claimed' | 'busy' | 'settled' | 'none';
  question?: string;
  answer?: string;
  guess?: string;
  options?: string[] | null;
}

/* ---- 2. Daily question: answer privately, reveal together ---- */

export type RevealState = 'pending' | 'revealed';

export interface QuestionDay {
  questionId: string;
  /** YYYY-MM-DD in the couple's day, one question per day */
  dateKey: string;
  prompt: string;
  state: RevealState;
  deadlineAt: number;
  revealedAt: number | null;
  answeredBy: string[]; // memberIds — reveals WHO answered, never WHAT
  /** Engine metadata (epic tC4MP1JArxmm). Optional so older payloads and the
   *  Thread card keep working; 'open' is the shape every pre-engine row has. */
  theme?: string;
  format?: QuestionFormatName;
  depth?: number;
  options?: string[] | null;
}

export type QuestionFormatName = 'open' | 'this_or_that' | 'scale';

/** Result of a skip or a "not for us" tap (PRD v2 §6). `swapped` means the
 *  day's question was replaced; otherwise the card shows the tomorrow state
 *  and `reason` says why (a partner already answered, or the daily ceiling). */
export interface QuestionSkipResult {
  swapped: boolean;
  reason?: 'answered' | 'limit';
  question: QuestionDay | null;
}

export interface QuestionAnswer {
  questionId: string;
  memberId: string;
  text: string;
  /**
   * Card LdgUHoQ4R4lS — true when this answer landed AFTER the reveal fired
   * (the 24h window had opened without it). Non-response stays recoverable:
   * the answer still saves and is still visible to both, it just reads as
   * late rather than as part of the ceremony.
   */
  late: boolean;
}

export interface RevealPayload {
  question: QuestionDay;
  /**
   * Revealed answers — normally 2, but a DEADLINE reveal can legitimately
   * carry 0..2 (whoever had answered by then). Answers never appear while
   * the question is pending, in any shape.
   */
  answers: QuestionAnswer[];
}

/**
 * Card jinkfGNSDI4j: what a 'question-card' message stores in `messages.text`.
 * ONE card per question — created once, rewritten in place on reveal.
 * `answers` appears ONLY when state === 'revealed'; while pending the row
 * carries `answeredBy` (who, never what) and nothing else about the answers.
 */
export interface QuestionCardPayload {
  v: 1;
  questionId: string;
  question: string;
  state: 'pending' | 'revealed';
  answeredBy: string[];
  answers?: { memberId: string; text: string; late?: boolean }[];
}

/**
 * Card ZdYBVP203b8a (PRD §6.3): what a 'date-card' message stores in
 * `messages.text` — the celebration moment when both partners are in.
 *
 * Posted once, on the affirm that closes the round. It is a REVEAL by
 * construction: a date card can only exist after the round is locked, so the
 * payload may carry the plan itself. Nothing here can leak an answer early —
 * there is no pre-lock state this message is written in.
 */
export interface DateCardPayload {
  v: 1;
  ideaId: string;
  title: string;
  placeName: string | null;
  mode: string;
  costBand: string;
  energy: string;
  /** The calendar write's outcome at lock time: 'written' | 'queued' | 'not_connected'. */
  calendarStatus: DateCalendarStatus;
  lockedAt: number;
}

/**
 * PRIVACY RULE (enforced server-side, probe-tested): answers are never in any
 * response before state === 'revealed'. Reveal happens when BOTH have answered
 * OR deadlineAt passes (whichever first). `answeredBy` may always be read —
 * knowing "you haven't answered yet" is the point, knowing the answer is not.
 *
 * SOLO RULE (card Pn1wrppZMWEr): a one-member couple has nobody to reveal to,
 * so the deadline half of that OR is suspended while the couple is solo —
 * solo answers stay pending and stack as sealed cards. The moment the partner
 * joins, deadline-passed pendings unlock (they reveal on the next read).
 */
export interface QuestionsAPI {
  /**
   * Today's question. `devUnseen` is the dev-only seam (404 in prod) that
   * draws today's prompt WITHOUT marking it seen, so a probe can reproduce
   * "nobody opened the app that day" and exercise the carry-forward rule
   * (card rA2HRCvDZSPA).
   */
  getTodayQuestion(now?: number, open?: boolean, devUnseen?: boolean): Promise<QuestionDay>;
  /** Fire the daily-question push at both members (card HWrVePJ5qW4d,
   *  follow-on slice 2). Dedupe lives in the room's own claim table, so the
   *  caller may call it on every fetch without stacking notifications. */
  notifyQuestionReady(questionId: string): Promise<void>;
  answerQuestion(questionId: string, memberId: string, text: string): Promise<{ question: QuestionDay; reveal: RevealPayload | null }>;
  /** Throws Error('not_revealed') while pending. */
  readQuestion(questionId: string, memberId: string): Promise<RevealPayload>;
  /** Revealed questions only, oldest first, each exactly once. */
  revealHistory(limit?: number): Promise<RevealPayload[]>;
  /** My sealed stack (solo accumulation) + the unlock sweep, see SealedPayload. */
  sealedAnswers(memberId: string): Promise<SealedPayload>;
  /** Skip today's question (PRD v2 §6). Swaps only while nobody has answered. */
  skipQuestion(questionId: string, memberId: string): Promise<QuestionSkipResult>;
  /** "Not for us": permanently opt the theme out, then behave like a skip. */
  notForUs(questionId: string, memberId: string): Promise<QuestionSkipResult>;
  /** DEV seam: replace today's unanswered question with one of this format,
   *  so a walk can assert a renderer the scheduler would only give by luck. */
  devForceFormat(format: string): Promise<QuestionDay>;
  /** DEV only (route is prod-guarded): queue counts for the generation probe. */
  queueSnapshot(): Promise<{ queued: number; generated: number; seeds: number; seen: number; recentPrompts: string[] }>;
}

/**
 * Card Pn1wrppZMWEr — one of MY answers whose question is still pending.
 * Carries no answer text: the card shows the prompt and the day, and that is
 * the whole point of a sealed stack (nothing opens until the partner is in).
 */
export interface SealedAnswer {
  questionId: string;
  dateKey: string;
  prompt: string;
  answeredAt: number;
}

/**
 * GET /api/questions/sealed — the solo accumulation payload.
 * `sealed` is mine only and never carries text; `count` is its length so the
 * stack's header is one field, not a client-side tally. `unlocked` is what
 * THIS call revealed: only a paired couple sweeps (a solo couple sweeps
 * nothing), so an empty `unlocked` on the solo side is the rule, not a miss.
 * Reveals are full RevealPayload — by then both can see them, and they are
 * the same objects Reveal history serves.
 */
export interface SealedPayload {
  sealed: SealedAnswer[];
  count: number;
  /** The couple has one member — nothing can reveal yet. */
  solo: boolean;
  /** Deadline-passed pendings this call just revealed, oldest first. */
  unlocked: RevealPayload[];
}

/* ---- 3. Per-category AI access toggles + audit log ---- */

export type AICategory = 'chat' | 'inbox' | 'questions' | 'vault' | 'todos' | 'interests' | 'digest' | 'recap' | 'dates';

/** 'recap' is last-but-one (card 3UwJvUtiirGN): walks and probes index this
 *  list from the top, and it doubles as the weekly recap's opt-in. 'dates'
 *  (card qyhVbyMpcN0B) is appended dead last for the same reason — the
 *  concierge's date-idea proposals are a separate permission from the rest. */
export const AI_CATEGORIES = ['chat', 'inbox', 'questions', 'vault', 'todos', 'interests', 'digest', 'recap', 'dates'] as const;

/** PRD §9 + decision 2026-10-01: every category starts OFF. */
export const AI_DEFAULT_TOGGLES: Readonly<Record<AICategory, boolean>> = {
  chat: false, inbox: false, questions: false, vault: false, todos: false, interests: false, digest: false, recap: false, dates: false,
};

export interface AIAccessLogEntry {
  id: string;
  category: AICategory;
  allowed: boolean;
  toggleState: boolean;
  reason: string;
  byMemberId: string | null;
  at: number;
}

export interface AITogglesAPI {
  getToggles(): Promise<Record<AICategory, boolean>>;
  setToggle(memberId: string, category: AICategory, on: boolean): Promise<Record<AICategory, boolean>>;
  /** Master switch (card hF6aXvFC0ZyN, Jonathan 2026-10-12): flips every
   *  category in ONE transaction so the couple never sees a half-applied
   *  batch. Per-category rows stay individually adjustable afterwards. */
  setAllToggles(memberId: string, on: boolean): Promise<Record<AICategory, boolean>>;
  /**
   * Enforcement point: every agent read MUST call this first.
   * Resolves true (logged allowed) or false (logged denied) — never throws for
   * a denied toggle, so callers can log-and-refuse in one place.
   */
  requestAiAccess(memberId: string | null, category: AICategory, reason: string): Promise<boolean>;
  aiAccessLog(limit?: number): Promise<AIAccessLogEntry[]>;
}

/* ---- 3b. Agent involvement: two paths, only offered once AI is on ----
 *
 * Decision (Jonathan, 2026-10-02) — this is the answer to card 6rFwbmKm8t5c
 * step 2 ("who generates cards, from what signal"):
 *   invoke → the couple explicitly tags the agent to speak; nothing is inferred
 *   auto   → a level-of-involvement slider decides how much it chimes in
 * Persona is deliberately reserved-but-unset ("to be defined later").
 *
 * This sits ON TOP of the per-category toggles, never instead of them:
 * toggles say which data the agent may touch (permission), this says how much
 * it speaks (behaviour). Both must be satisfied for a card to appear.
 */

export type AgentMode = 'invoke' | 'auto';

export const AGENT_MODES = ['invoke', 'auto'] as const;

/** Least surprise: the agent never speaks unasked until the couple opts in. */
export const AGENT_MODE_DEFAULT: AgentMode = 'invoke';

/** 0 = silent, 4 = frequently. Only meaningful when mode === 'auto'. */
export const AGENT_INVOLVEMENT_MAX = 4;
export const AGENT_INVOLVEMENT_DEFAULT = 0;

export interface AgentSettings {
  mode: AgentMode;
  /** 0..AGENT_INVOLVEMENT_MAX */
  involvement: number;
  /** Reserved for the persona work ("to be defined later"). */
  persona: string | null;
  updatedAt: number;
  updatedBy: string | null;
}

export interface AgentSettingsPatch {
  mode?: AgentMode;
  involvement?: number;
}

export interface AgentSettingsAPI {
  getAgentSettings(): Promise<AgentSettings>;
  setAgentSettings(memberId: string, patch: AgentSettingsPatch): Promise<AgentSettings>;
}

/* ---- 4. Shared Notebook: paste link → card, unfurl, agent categorize ---- */

/** Epic szvNTWusHt3f (Jonathan's feature addition, not in PRD §6/§7).
 *  'trailer' stopped being a category (card 3ThoVHSXNpjo): a trailer is filed
 *  under the thing it trails — TV or a movie. Nothing may write it back: the
 *  API validates against NOTEBOOK_CATEGORIES, the model prompt is built from
 *  it, classify() never returns it, and the durable object blocks it at the
 *  schema and trigger level. */
export type NotebookCategory =
  | 'movie'
  | 'tv'
  | 'music'
  | 'podcast'
  | 'video'
  | 'article'
  | 'other';

export const NOTEBOOK_CATEGORIES = [
  'movie', 'tv', 'music', 'podcast', 'video', 'article', 'other',
] as const;

/** Notebook v2 typed collections (design v1.1 §5.3 / §7): a saved thing is a
 *  link (paste) or a plain-text quick add. entry_type names the collection it
 *  belongs to; category stays the classifier's media verdict for links. */
export type NotebookEntryType = 'link' | 'gift' | 'memory' | 'date' | 'note' | 'idea';
export const NOTEBOOK_ENTRY_TYPES = ['link', 'gift', 'memory', 'date', 'note', 'idea'] as const;

/** Every card links out by intent: movie→play, album→stream, podcast→play, article→read. */
export type NotebookIntent = 'play' | 'stream' | 'read' | 'open';

/* ── Links v1 data model (card upbzFPmJUl7Q, PRD §5/§7) ───────────────────────
 * "Types are data, destinations are views": one link object carries a kind and
 * a subtype, and Dates / Gift ideas / Notebook / Do together are views over it,
 * never copies. The kind is the closed top-level set (the chips); the subtype
 * is open-ended and falls back to the kind when unknown. Facets (status, scope,
 * intent, place, when, saved-by, tags) apply to every kind.
 *
 * Validation lives here, not in SQL CHECK constraints: a constraint an older
 * table cannot satisfy takes the whole Durable Object room down at boot (see the
 * hazard note at the top of NOTEBOOK_SCHEMA_SQL), and the new columns are added
 * to tables that predate them. */
export type NotebookLinkKind =
  | 'watch'
  | 'listen'
  | 'read'
  | 'play'
  | 'learn'
  | 'mementos'
  | 'places'
  | 'events'
  | 'trips'
  | 'things'
  | 'make'
  | 'admin'
  | 'other';

/** Chip order is the founder's (2026-10-17): Watch · Listen · Read · Play ·
 *  Learn · Mementos, then the rest. NOTEBOOK_LINK_KINDS is also the display
 *  order, so this array is load-bearing. */
export const NOTEBOOK_LINK_KINDS = [
  'watch', 'listen', 'read', 'play', 'learn', 'mementos',
  'places', 'events', 'trips', 'things', 'make', 'admin', 'other',
] as const;

/** Kind → the subtypes the PRD names as examples (PRD §5 layer 1). Open-ended:
 *  a subtype not in this list is still stored, and the UI falls back to the kind.
 *  Founder's call 2026-10-17: Learn owns courses and tutorials, so Make no
 *  longer lists tutorials (Make is recipes and DIY). */
export const NOTEBOOK_LINK_SUBTYPES: Record<NotebookLinkKind, readonly string[]> = {
  watch: ['movies', 'tv', 'documentaries', 'video'],
  listen: ['podcasts', 'albums', 'playlists'],
  read: ['books', 'articles', 'newsletters'],
  play: ['video-games', 'board-games'],
  learn: ['courses', 'tutorials'],
  mementos: ['photo-albums', 'keepsakes'],
  places: ['restaurants', 'cafes', 'bars', 'parks', 'venues', 'attractions'],
  events: ['gigs', 'theatre', 'comedy', 'exhibitions', 'festivals', 'markets', 'screenings'],
  trips: ['destinations', 'stays', 'itineraries'],
  things: ['gifts', 'home', 'clothes', 'tech'],
  make: ['recipes', 'diy'],
  admin: ['bookings', 'tickets', 'property', 'forms'],
  other: [],
};

export const isNotebookLinkKind = (v: unknown): v is NotebookLinkKind =>
  typeof v === 'string' && (NOTEBOOK_LINK_KINDS as readonly string[]).includes(v);

/** Facet: lifecycle (PRD §6.6). Events and trips carry a date and auto-move to
 *  done once past; planned is set when a date or plan is affirmed by both. */
export type NotebookLinkStatus = 'saved' | 'planned' | 'done' | 'passed';
export const NOTEBOOK_LINK_STATUSES = ['saved', 'planned', 'done', 'passed'] as const;

export const isNotebookLinkStatus = (v: unknown): v is NotebookLinkStatus =>
  typeof v === 'string' && (NOTEBOOK_LINK_STATUSES as readonly string[]).includes(v);

/** Facet: who can see it (PRD §6.4). Scope is decided by the routing table,
 *  never by the classifier's confidence alone — gifts and products default to
 *  just-me, and me→shared is always a deliberate action. */
export type NotebookLinkScope = 'shared' | 'just_me';
export const NOTEBOOK_LINK_SCOPES = ['shared', 'just_me'] as const;

/** How the link got in (PRD §6.1 entry points). */
export type NotebookLinkSource = 'app' | 'share' | 'email';
export const NOTEBOOK_LINK_SOURCES = ['app', 'share', 'email'] as const;

export const isNotebookLinkSource = (v: unknown): v is NotebookLinkSource =>
  typeof v === 'string' && (NOTEBOOK_LINK_SOURCES as readonly string[]).includes(v);

/* ── Routing table (card 4CXp7HfSIAGd, PRD §6.3/§6.4) ────────────────────────
 * Every kind has ONE home — the surface its cards appear on — plus the default
 * scope and the next action its card offers. Destinations are views over one
 * object ("types are data"), so this table never copies a row: it answers where
 * the same row shows up and what you can do with it next.
 *
 * Scope is decided HERE, never by the classifier's confidence (PRD §6.4): a
 * just-me default is the one decision that cannot be undone quietly, so a kind
 * that defaults private (Things — gifts and products) is tightened to just-me
 * by the writer, and only a deliberate human action may widen it back.
 *
 * Home is a view id, not a route: the Notebook screen renders every kind as a
 * chip, and the destinations (Dates, Gift ideas, Do together, Requests) read
 * the same rows. Adding a destination means adding an id here and a view that
 * consumes it — never a second copy of the link. */
export type NotebookLinkHome =
  /** The links index itself — saved cards, kind chips, search. */
  | 'notebook'
  /** Gift ideas — private by default so a surprise stays a surprise. */
  | 'gift_ideas'
  /** Dates › Places (map pin). */
  | 'dates_places'
  /** Dates › Ideas. */
  | 'dates_ideas'
  /** Do together › Plans. */
  | 'plans'
  /** Do together › Cook / Projects. */
  | 'make'
  /** Requests (life admin) › follow-ups. */
  | 'requests';

/** What the card's primary button offers (PRD §6.3 "next action"). */
export type NotebookNextAction =
  | 'nominate_date'
  | 'watch_together'
  | 'listen_together'
  | 'read_together'
  | 'play_together'
  | 'learn_together'
  | 'revisit'
  | 'add_to_wishlist'
  | 'start_plan'
  | 'add_to_plan'
  | 'cook_it'
  | 'create_task'
  | 'reclassify';

export interface NotebookLinkRoute {
  /** The kind's own name, for the chip row (the founder's taxonomy: Watch ·
   *  Listen · Read · Play · Learn · Mementos, then the rest). The home label is
   *  where it files, which is not always the same word — Watch and Listen both
   *  file under Notebook › Media, and a row of two "Media" chips tells you
   *  nothing. */
  kindLabel: string;
  home: NotebookLinkHome;
  /** The destination as a person reads it — receipt copy and group headings. */
  homeLabel: string;
  defaultScope: NotebookLinkScope;
  nextAction: NotebookNextAction;
  /** The verb on the card's primary button. */
  nextActionLabel: string;
}

/** Kind → route. Every kind in NOTEBOOK_LINK_KINDS MUST appear here: the probe
 *  asserts the key set against the chip order, so a new kind cannot ship with
 *  no home. */
export const NOTEBOOK_LINK_ROUTING: Record<NotebookLinkKind, NotebookLinkRoute> = {
  watch: {
    kindLabel: 'Watch',
    home: 'notebook', homeLabel: 'Notebook › Media', defaultScope: 'shared',
    nextAction: 'watch_together', nextActionLabel: 'Watch together',
  },
  listen: {
    kindLabel: 'Listen',
    home: 'notebook', homeLabel: 'Notebook › Media', defaultScope: 'shared',
    nextAction: 'listen_together', nextActionLabel: 'Listen together',
  },
  read: {
    kindLabel: 'Read',
    home: 'notebook', homeLabel: 'Notebook › Reading', defaultScope: 'shared',
    nextAction: 'read_together', nextActionLabel: 'Read together',
  },
  play: {
    kindLabel: 'Play',
    home: 'notebook', homeLabel: 'Notebook › Games', defaultScope: 'shared',
    nextAction: 'play_together', nextActionLabel: 'Play together',
  },
  learn: {
    kindLabel: 'Learn',
    home: 'notebook', homeLabel: 'Notebook › Learning', defaultScope: 'shared',
    nextAction: 'learn_together', nextActionLabel: 'Learn together',
  },
  mementos: {
    kindLabel: 'Mementos',
    home: 'notebook', homeLabel: 'Notebook › Mementos', defaultScope: 'shared',
    nextAction: 'revisit', nextActionLabel: 'Revisit it',
  },
  places: {
    kindLabel: 'Places',
    home: 'dates_places', homeLabel: 'Dates › Places', defaultScope: 'shared',
    nextAction: 'nominate_date', nextActionLabel: 'Nominate for a date',
  },
  events: {
    kindLabel: 'Events',
    home: 'dates_ideas', homeLabel: 'Dates › Ideas', defaultScope: 'shared',
    nextAction: 'nominate_date', nextActionLabel: 'Nominate for a date',
  },
  trips: {
    kindLabel: 'Trips',
    home: 'plans', homeLabel: 'Do together › Plans', defaultScope: 'shared',
    nextAction: 'start_plan', nextActionLabel: 'Start a plan',
  },
  things: {
    kindLabel: 'Gifts',
    home: 'gift_ideas', homeLabel: 'Gift ideas', defaultScope: 'just_me',
    nextAction: 'add_to_wishlist', nextActionLabel: 'Add to wishlist',
  },
  make: {
    kindLabel: 'Make',
    home: 'make', homeLabel: 'Do together › Cook & Projects', defaultScope: 'shared',
    nextAction: 'cook_it', nextActionLabel: 'Add to a plan',
  },
  admin: {
    kindLabel: 'Admin',
    home: 'requests', homeLabel: 'Requests', defaultScope: 'shared',
    nextAction: 'create_task', nextActionLabel: 'Create a follow-up',
  },
  other: {
    kindLabel: 'Other',
    home: 'notebook', homeLabel: 'Notebook › Saved', defaultScope: 'shared',
    nextAction: 'reclassify', nextActionLabel: 'Reclassify',
  },
};

/** Unknown kind (a row written before the table, or a future kind) falls back
 *  to the catch-all route rather than throwing: a read must never fail because
 *  a card is unusual. */
export const notebookRouteFor = (kind: string): NotebookLinkRoute =>
  (NOTEBOOK_LINK_ROUTING as Record<string, NotebookLinkRoute>)[kind] ?? NOTEBOOK_LINK_ROUTING.other;

/** Does filing this kind tighten the row to private? True only for a kind whose
 *  route defaults to just-me — the writer applies it in ONE direction, so a
 *  shared row never becomes private by accident and a private row is never
 *  published by a classifier. */
export const notebookRouteTightensScope = (kind: string): boolean =>
  notebookRouteFor(kind).defaultScope === 'just_me';

/** One row per real-world thing a link points at (PRD §7). Multiple links to
 *  the same entity merge, and "both saved" is an entity both partners linked. */
export interface NotebookEntity {
  id: string;
  kind: NotebookLinkKind;
  name: string;
  canonicalUrl: string | null;
  place: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}


export interface NotebookItem {
  itemId: string;
  url: string;
  /** Unfurled title; null until the metadata fetch resolves (never blocks the paste). */
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  durationSeconds: number | null;
  /** youtube | spotify | apple-podcasts | letterboxd | imdb | article | unknown */
  provider: string;
  category: NotebookCategory;
  /** Why it was categorized this way — contract: non-empty whenever category != 'other'. */
  categoryReason: string;
  /** null = partner has not classified it either (manual tagging path). */
  categorizedBy: string | null;
  intent: NotebookIntent;
  /** Shared with partner by default (PRD §4 principle 3). */
  shared: boolean;
  /** Set by either partner; reflects to the other on reconnect. */
  doneAt: number | null;
  addedBy: string;
  createdAt: number;
  updatedAt: number;
  /** Notebook v2 collection this entry belongs to; 'link' for any paste. */
  entryType: NotebookEntryType;
  /** Plain-text body for gift/memory/date/note/idea quick adds; null for links. */
  body: string | null;
  /** Gift-suggestion enrichment state (card OviOTaNsNivQ). '' = not a gift
   *  (or a row saved before the feature); 'pending' = the search is running;
   *  'done' = detail landed; 'off' = concierge toggle off, nothing ran;
   *  'none' = the search ran and came back empty. */
  enrichStatus: GiftEnrichStatus;
  /** Parsed `enrichment` column — null unless enrichStatus === 'done'. */
  enrichment: GiftEnrichment | null;
  /** The plan session that spawned this entry (card 7aTCRQ9j8PWV, design §6);
   *  null on anything not filed from a weekly meeting. */
  sourceEventId: string | null;
  /* ── Links v1 (card upbzFPmJUl7Q, PRD §5/§7). Written alongside the legacy
   *  category/entry_type/shared columns, which stay authoritative until the
   *  reader cards switch over. */
  /** Top-level kind — the chip the link files under. */
  kind: NotebookLinkKind;
  /** Free-form subtype under the kind; null until the classifier or the user
   *  picks one (the UI falls back to the kind). */
  subtype: string | null;
  /** The real-world thing this link points at; null until entity resolution
   *  lands (PRD §6.5, "Next" phase). */
  entityId: string | null;
  status: NotebookLinkStatus;
  scope: NotebookLinkScope;
  /** City or "near us" (PRD §5 layer 3). */
  place: string | null;
  /** Event date or expiry, epoch ms; events and trips only. */
  eventDate: number | null;
  source: NotebookLinkSource | null;
  /** Classifier confidence 0..1; null on rows saved before the model existed. */
  confidence: number | null;
  /** Why the cascade filed this kind (card 5ZQIfujFJMjE) — the receipt shows
   *  it verbatim, and it is separate from the media-category reason. */
  kindReason: string | null;
  /** The band the confidence produced ('auto' | 'check' | 'ask'); null on a
   *  row the cascade never saw. An 'ask' row is not a confident filing. */
  kindTier: string | null;
  /** For 'ask': the 2-4 ranked candidates the chooser offers, best guess
   *  first. null when the cascade had nothing to offer. */
  kindOptions: Array<{ kind: NotebookLinkKind; subtype: string | null; confidence: number | null }> | null;
}

/**
 * Gift enrichment — what the concierge found when it went shopping for the
 * idea the user just saved (card OviOTaNsNivQ). Every field is read off the
 * engine's own rows: the price band is the min/max of the priced matches,
 * `why` quotes those rows back, and `picks` are the first cards that carried
 * a link. Nothing here is invented when the search returns nothing — that
 * is `enrichStatus: 'none'`.
 */
export type GiftEnrichStatus = '' | 'pending' | 'done' | 'off' | 'none';

export interface GiftEnrichmentPick {
  title: string;
  /** Formatted for display ('A$349.00') — null when the row had no price. */
  price: string | null;
  url: string;
  /** '4.5/5 from 2,150' — null when the row carried no rating. */
  rating: string | null;
  /** Card art when the engine sent one (card Yw5UFURMXSTY). */
  image: string | null;
  /** 'Overall Pick', 'Best Seller' — a label, never a bare flag. */
  badge: string | null;
  /** The engine's delivery line, verbatim — the cheapest possible delivery
   *  advisor (card Yw5UFURMXSTY): no carrier API, the row already carries it. */
  availability: string | null;
  /** Card ggTQe2KbB0A3 MEDIUM-1 — the sentence explaining THIS pick, built
   *  from the same product row the card renders. Without it every card in
   *  the stack showed the recommendation's one rationale, so flicking to
   *  product two read product one's reason. Null on rows stored before the
   *  field existed — the client falls back to the rec-level rationale. */
  why?: string | null;
}

export interface GiftEnrichment {
  /** Which key actually served the query — recorded, not assumed. */
  source: 'serpapi' | 'apiserpent' | 'serppost';
  /** The query sent, so the sentence below can be checked against it. */
  query: string;
  /** 'A$68.00 – A$1,299.00' across the priced matches. */
  priceBand: string;
  /** Why it fits — built only from the rows the engine returned. */
  why: string;
  /** Top three matches that carried a link. */
  picks: GiftEnrichmentPick[];
  /** How many matches the page held. */
  count: number;
  at: number;
}

/**
 * Tags a partner (or the agent, once accepted) hangs off a saved entry —
 * card ioKcDXGeHnE7: mood / theme / time, suggested by the scan, applied
 * only by a human. The category column stays what the entry IS; these are
 * what it feels like / is about / when it belongs to.
 */
export type NotebookTagKind = 'mood' | 'theme' | 'time';
export const NOTEBOOK_TAG_KINDS = ['mood', 'theme', 'time'] as const;

/**
 * Closed theme vocabulary (card 5fEg3YWwWZhF — anti-category-creep).
 *
 * A theme is what an entry is ABOUT at a human level. The specific subject
 * (aviation, football, true crime) is never a theme: it is a detail, and it
 * is only kept when it matches an interest already in a partner's profile.
 * The model picks from this list and nothing else — anything outside it is
 * dropped before storage, so the filter list stays readable forever.
 *
 * Widening the set is a deliberate human edit HERE, never a model decision.
 */
export const NOTEBOOK_THEME_VOCAB = [
  'news',
  'thought-provoking',
  'how-to',
  'story',
  'comedy',
  'chill',
  'big-feeling',
  'learn-something',
  'plan-something',
] as const;

export type NotebookTheme = (typeof NOTEBOOK_THEME_VOCAB)[number];

export function isNotebookTheme(tag: string): tag is NotebookTheme {
  return (NOTEBOOK_THEME_VOCAB as readonly string[]).includes(tag);
}

/**
 * Editorial wording for the theme keys (card CTVSTgXykQ0Y, carried by
 * BS0NIMpbbuC5) — ONE vocabulary for the whole app: the notebook card, the
 * digest's source list and any API consumer read THIS map. The stored tag,
 * the filter key, the API payload and every data-* attribute keep the RAW
 * key (NOTEBOOK_THEME_VOCAB), so search and filters never depend on prose.
 * A key missing from the map (a word someone typed themselves) falls back to
 * the raw key — never blank.
 */
export const NOTEBOOK_THEME_LABELS: Record<string, string> = {
  news: 'In the news',
  'thought-provoking': 'Worth a thought',
  'how-to': 'How to do it',
  story: 'A good story',
  comedy: 'Something funny',
  chill: 'Something easy',
  'big-feeling': 'Felt this one',
  'learn-something': 'Learn something',
  'plan-something': 'Something to plan',
};

/** Display-only render of a theme key; mood/time tags print as typed. */
export function notebookThemeLabel(tag: string): string {
  return NOTEBOOK_THEME_LABELS[tag] ?? tag;
}

export interface NotebookTag {
  id: string;
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  /** 'human' typed by a partner, 'agent' applied from an accepted suggestion. */
  source: 'human' | 'agent';
  addedBy: string | null;
  createdAt: number;
}

export type NotebookSuggestionStatus = 'pending' | 'accepted' | 'overridden' | 'dismissed';
export const NOTEBOOK_SUGGESTION_STATUSES = ['pending', 'accepted', 'overridden', 'dismissed'] as const;

export interface NotebookTagSuggestion {
  id: string;
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  /** Why the agent proposed it — required on every row, rendered verbatim. */
  rationale: string;
  /**
   * The concrete subject the model saw, kept ONLY when it matches an interest
   * in a partner's profile (card 0OvyJbpnACMX); null otherwise. Never a tag —
   * the closed theme vocabulary owns tags, this is the "about aviation" note.
   */
  subject: string | null;
  status: NotebookSuggestionStatus;
  scanAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  createdAt: number;
}

/** One proposed tag coming back from the model; validated before it is stored. */
export interface NotebookTagProposal {
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  rationale: string;
  /** The concrete subject the model saw (aviation, football). Never stored as
   *  a theme — only kept when it matches an interest in a partner's profile
   *  (card 0OvyJbpnACMX), and dropped otherwise. */
  subject?: string;
}

export interface NotebookAPI {
  /** Paste a URL → card created immediately (raw URL), unfurl runs async. */
  /** `gift` is the gift toggle (card PQX46vNPdbwn): it files the paste under
   *  Things › Gifts, which the routing table defaults to just-me; `shared: true`
   *  is the user deliberately widening that link to the couple. */
  addNotebookItem(input: { url: string; addedBy: string; gift?: boolean; shared?: boolean }): Promise<NotebookItem>;
  /** Notebook v2 quick add (design v1.1 §5.3): a typed plain-text entry
   *  (gift/memory/date/note/idea). url is empty — the body IS the entry. */
  addQuickEntry(input: {
    type: NotebookEntryType;
    body: string;
    addedBy: string;
    /** Visibility for typed entries (card sYwPmYEIf4VC): 'us' (default) is
     *  shared with the partner, 'me' is private to `addedBy`. Links are
     *  always shared — they are what the notebook is for. */
    shared?: boolean;
    /** Sub-type marker for a typed entry (meeting transcription card
     *  a0ABwZvTf9ct sets 'meeting'). Plain text stays 'plain-text'. This is
     *  the column `provider` already had — using it instead of a new
     *  entry_type is deliberate: a new entry_type needs a CHECK-constraint
     *  migration on every couple's notebook table. */
    provider?: string;
    /** Audio length in seconds for a transcription card (NULL for anything
     *  hand-typed). Stored in the existing duration_seconds column. */
    durationSeconds?: number | null;
    /** Gift idea only (card OviOTaNsNivQ): 'pending' when the concierge is
     *  on and a search will run after the response, 'off' when it will not.
     *  Anything else leaves the row at '' (not a gift / not attempted). */
    enrichStatus?: string;
    /** Server-side idempotency key for the event this entry files (card
     *  eq92fFXqQUuP, sol review 2026-10-19): the caller can read it back to
     *  ask "did this event already file?" before inserting, so a crash or a
     *  concurrent retry cannot duplicate the entry. Omitted by every
     *  pre-existing caller. */
    sourceEventId?: string;
  }): Promise<NotebookItem>;
  /** Write the gift-enrichment result back onto a gift card — the status
   *  ('done' | 'none' | …) plus the detail JSON, or null for 'none'. Gift
   *  rows only; anything else answers `not_a_gift`. */
  setGiftEnrichment(itemId: string, status: string, enrichment: GiftEnrichment | null): Promise<NotebookItem>;
  /** Remove a user-authored entry outright (gift/memory/date/note/idea). */
  /** `viewerId` is enforced: a member cannot act on a card they cannot see
   *  (a private card answers not_found, so its existence never leaks). */
  removeNotebookEntry(itemId: string, viewerId: string): Promise<NotebookItem>;

  /** Card 67zOnRgx4YTI item 1 — the saver rewrites their own card's words
   *  (link: title/description, typed entry: body). Same gate as remove. */
  editNotebookEntry(
    itemId: string,
    viewerId: string,
    patch: {
      title?: string;
      description?: string;
      body?: string;
      /* Card 2qBAZOM2LFWa — a card's own detail (the day, the place). Both
         are nullable: `null` clears, `undefined` leaves the row alone. */
      eventDate?: number | null;
      place?: string | null;
    },
  ): Promise<NotebookItem>;
  /** The list as `viewerId` may see it: every shared card plus their own
   *  private ones. Private cards are filtered here (not at the edge) so no
   *  future caller can forget. */
  listNotebook(viewerId: string): Promise<NotebookItem[]>;
  /** Called by the worker once unfurl returns; merge metadata, keep raw URL on failure.
   *  `intent` is OPTIONAL and defaults to "leave the column alone" (card
   *  QqIIMN87A0RB): the verb on a card is a classification, and a caller that
   *  only has metadata to add — settleFiledLink — must not be able to restyle
   *  it. The unfurl doors that DO classify pass it explicitly. */
  setNotebookMeta(itemId: string, meta: {
    title: string | null;
    description: string | null;
    imageUrl: string | null;
    durationSeconds: number | null;
    provider: string;
    intent?: NotebookIntent;
  }): Promise<NotebookItem>;
  /** Agent or manual classification. Empty reason throws Error('reason_required'). */
  categorizeNotebook(itemId: string, category: NotebookCategory, reason: string, byMemberId: string | null): Promise<NotebookItem>;
  /** Links v1 cascade filing (card 5ZQIfujFJMjE, PRD §6.2): writes kind,
   *  subtype, the classifier's confidence, and the band + ranked options.
   *  Links only — a quick-add answers not_a_link. A member's own retag
   *  (categorized_by set) answers overridden_by_member: a human decision
   *  always beats the background filing. Empty reason on a filed kind
   *  throws Error('reason_required'), same contract as categorizeNotebook. */
  setLinkFiling(itemId: string, filing: {
    kind: NotebookLinkKind;
    subtype: string | null;
    confidence: number;
    reason: string;
    tier: string;
    options: Array<{ kind: NotebookLinkKind; subtype: string | null; confidence: number }>;
  }): Promise<NotebookItem>;
  /** The chooser's write (card xNGOA5rZz9LV, sol review 2026-10-19): a person
   *  answering an 'ask' row files the kind themselves — any member who can see
   *  the card, same contract as categorizeNotebook (empty reason on a filed kind
   *  throws Error('reason_required')). Links only — a quick-add answers
   *  not_a_link. Success clears kind_tier/kind_options, so the chooser leaves
   *  with the question it answered, and sets categorized_by, which makes the
   *  decision terminal for the background cascade. */
  fileNotebookKind(itemId: string, viewerId: string, kind: NotebookLinkKind, subtype: string | null, reason: string): Promise<NotebookItem>;
  /** watched/played/done — toggled by either partner. */
  markNotebookDone(itemId: string, done: boolean, viewerId: string): Promise<NotebookItem>;
  /** Items whose metadata changed since the scan cursor — the scan's working set. */
  listNotebookSince(since: number, limit?: number): Promise<NotebookItem[]>;
  /** Tags already on an entry; omit itemId for every tag in the notebook.
   *  Scoped: `viewerId` gates the per-item read and joins the notebook-wide
   *  list through the visibility seam (card fyOZFYwAtbZy), so tags derived
   *  from a partner's just-me row never leave the room. */
  listNotebookTags(viewerId: string, itemId?: string): Promise<NotebookTag[]>;
  /** Deliberate visibility change, OWNER only (card fyOZFYwAtbZy, PRD §8):
   *  me→us publishes to the partner, us→me takes it back. A partner's
   *  private row answers not_found (existence never leaks); a shared row
   *  that isn't theirs answers forbidden. */
  setNotebookVisibility(itemId: string, viewerId: string, shared: boolean): Promise<NotebookItem>;
  /** Suggestions the viewer may see (scoped through the notebook row, card
   *  fyOZFYwAtbZy); omit status for pending only. */
  listTagSuggestions(viewerId: string, status?: NotebookSuggestionStatus): Promise<NotebookTagSuggestion[]>;
  /** Persist one scan's proposals; a duplicate (item, kind, tag) is skipped. */
  recordTagSuggestions(input: { proposals: NotebookTagProposal[]; scanAt: number }): Promise<NotebookTagSuggestion[]>;
  /** Human accept (apply the agent's tag) or override (apply their own word). */
  resolveTagSuggestion(input: {
    id: string;
    action: 'accept' | 'override';
    tag?: string;
    memberId: string;
  }): Promise<NotebookTagSuggestion>;
  /** Last successful scan timestamp (0 = never), for the daily cursor. */
  scanCursor(): Promise<number>;
  setScanCursor(ts: number): Promise<void>;
}

/**
 * Outcome of one notebook tag scan (card ioKcDXGeHnE7). `scanned` is true
 * only when the model actually ran; every other stop names why it did not,
 * so a probe can assert "toggle off = no scan" without reading the tables.
 */
export type NotebookScanStop =
  | 'not_plus'
  | 'toggle_off'
  | 'no_model_key'
  | 'no_new_items'
  | 'model_error'
  | 'emitted'
  | 'no_signal';

export interface NotebookScanResult {
  scanned: boolean;
  reason: NotebookScanStop;
  /** Suggestions persisted by this run. */
  count: number;
  /** Entries the run considered. */
  considered: number;
}

/** Lives on the DO itself (it needs env + the message fan-out), not on the slice. */
export interface NotebookScanAPI {
  runNotebookScan(): Promise<NotebookScanResult>;
}

/* ---- 4b. Couple inbox (card 17XyniccuSCt, Phase A: ingest + triage) ---- */

/** SES classes a message into one of these; "other" is the honest bucket. */
export type InboxCategory = 'bill' | 'booking' | 'notice' | 'receipt' | 'other';
export const INBOX_CATEGORIES = ['bill', 'booking', 'notice', 'receipt', 'other'] as const;

/** Where the card lands: both partners, or just the one it is addressed to. */
export type InboxRoute = 'both' | 'single';

/** Where an ingested message can be filed by the content router (card
 *  99VNKrT2nwvy). 'notebook' means a link card (the media verdict lives in
 *  the notebook row's category); memory/idea are typed quick adds. 'inbox'
 *  is the honest "this is administrative mail, leave it where it is". */
export type InboxSurface = 'notebook' | 'memory' | 'idea' | 'inbox';
export const INBOX_SURFACES = ['notebook', 'memory', 'idea', 'inbox'] as const;
/** The surfaces a filed item may be MOVED to — the sibling areas, never inbox
 *  (an item that never left the inbox has nothing to move). */
export const INBOX_MOVE_TARGETS = ['notebook', 'memory', 'idea'] as const;

export interface InboxItem {
  itemId: string;
  /** Upstream message id — repeated deliveries dedupe onto the first row. */
  messageId: string | null;
  sender: string;
  subject: string;
  snippet: string;
  category: InboxCategory;
  /** Why it was classified this way — shown verbatim (PRD §6). */
  categoryReason: string;
  route: InboxRoute;
  /** null when route = 'both'. */
  routedTo: string | null;
  /** Why it went where it went — shown verbatim, same contract. */
  routeReason: string;
  /** Rate limit tripped: held for review instead of delivered. */
  quarantined: boolean;
  quarantineReason: string | null;
  receivedAt: number;
  /** Content routing (card 99VNKrT2nwvy): where the agent filed this message,
   *  the id of the entry it wrote there, and the sentence that says why.
   *  All three null until the router files it; all three move together. */
  filedSurface: InboxSurface | null;
  filedItemId: string | null;
  filedReason: string | null;
  /** Low-confidence verdict: the agent did NOT file it, but names where it
   *  thinks it belongs so a person can move it in one tap. */
  suggestedSurface: InboxSurface | null;
  suggestedReason: string | null;
  /** Chore intake (card UZDxtHiJhtRt): a parsed $/deadline proposal waiting
   *  on a person, and the id of the chore it became once confirmed. Null
   *  until a message proposes something — the gate is a confirmation, never
   *  an automatic task. */
  choreProposal: {
    title: string;
    dueAt: number | null;
    freq: ChoreFreq | null;
    amount: number | null;
    currency: string | null;
    reason: string;
    state: 'pending' | 'confirmed' | 'declined';
  } | null;
  choreId: string | null;
  /** Lane (card QLPMbGu3EiXm): `me` = private to `ownerMemberId`, `us` =
   *  shared by the couple. Null on the legacy couple-code lane. */
  lane: InboxLane | null;
  ownerMemberId: string | null;
}

/** The two fixed address lanes — same for every couple, no codes. */
export type InboxLane = 'me' | 'us';

export interface InboxAPI {
  /** Classify + route + persist. Returns deduped:true on a repeated message id. */
  ingestInbox(input: {
    messageId: string | null;
    sender: string;
    subject: string;
    snippet: string;
    category: InboxCategory;
    categoryReason: string;
    /** Lane (card QLPMbGu3EiXm): `me` routes to `ownerMemberId` alone, `us`
     *  routes to both. Null on the legacy couple-code lane. */
    lane?: InboxLane | null;
    ownerMemberId?: string | null;
    /** Security hold (card YYrJc5OkW7EP): present → the item is quarantined
     *  with this sentence as the reason, instead of being routed. Set by the
     *  sender allowlist; the rate limit below is the other holder. */
    holdReason?: string;
  }): Promise<{ item: InboxItem; deduped: boolean }>;
  /** Newest first. Items routed to the OTHER partner are not returned. */
  listInbox(memberId: string): Promise<InboxItem[]>;
  /** Content routing (card 99VNKrT2nwvy): record where the agent filed this
   *  message, and the sentence that says why. Clears any prior suggestion —
   *  a filed item is no longer ambiguous. */
  setInboxFiling(itemId: string, filed: {
    surface: InboxSurface;
    entryId: string;
    reason: string;
  }): Promise<InboxItem>;
  /** Low-confidence verdict: name where it belongs WITHOUT writing there. */
  setInboxSuggestion(itemId: string, suggested: {
    surface: InboxSurface;
    reason: string;
  }): Promise<InboxItem>;
  /** The "move to" affordance (card 99VNKrT2nwvy): re-file this message to a
   *  sibling surface. Creates the entry on `to`, deletes the entry the agent
   *  itself wrote (never anyone else's row), and rewrites the filing columns.
   *  Returns the updated inbox item plus the entry it now points at. */
  moveInboxItem(itemId: string, to: InboxSurface, memberId: string): Promise<{
    item: InboxItem;
    entry: NotebookItem;
  }>;
  /** Chore intake (card UZDxtHiJhtRt): record a parsed $/deadline proposal
   *  against this message. Returns null when the row already carries a
   *  decision — a proposal is written exactly once per message. */
  setInboxProposal(itemId: string, proposal: {
    title: string;
    dueAt: number | null;
    freq: ChoreFreq | null;
    amount: number | null;
    currency: string | null;
    reason: string;
  }): Promise<InboxItem | null>;
  /** A person said yes: the pending proposal becomes a real chore (created
   *  through the chores slice, so Google sync and assign rules apply), the
   *  sender is stamped trusted for next time, and the row records the chore
   *  id. Throws not_found / no_pending_proposal / already_decided. */
  confirmInboxChore(itemId: string, memberId: string): Promise<{ item: InboxItem; chore: ChoreItem }>;
  /** A person said no (misparse feedback): the proposal is declined and the
   *  sender domain is stamped, so future mail from it proposes nothing.
   *  Throws not_found / no_pending_proposal / already_decided. */
  declineInboxChore(itemId: string, reason?: string): Promise<InboxItem>;
  /** Per-sender trust lookup used by the ingest path: a sender with a
   *  confirmation and no decline skips the gate on its next $/deadline
   *  message; a declined sender proposes nothing at all. */
  senderChoreTrust(senderDomain: string): Promise<{ confirmed: number; declined: number }>;
  /** Stamp a sender's trust row (called by confirm/decline above). */
  recordSenderChoreTrust(senderDomain: string, verdict: 'confirmed' | 'declined'): Promise<void>;
  /** The re-route affordance (card NWmSqIsojQyU): a person changes where a
   *  message landed — both, or one named member — and future mail from THAT
   *  SENDER ADDRESS (full mailbox, not the domain — sol review 2026-10-15)
   *  follows the same verdict. A private (me@) item is never moved: a
   *  message the partner can see is not private. The stamped override also
   *  wins over a later `us@` send from the same mailbox, because the
   *  couple's own verdict beats the address the sender picked; the UI says
   *  so beside the action. `memberId` is persisted as `decided_by` — the
   *  audit trail of which partner chose it.
   *  Throws not_found / quarantined / private_item / unknown_member. */
  rerouteInboxItem(itemId: string, to: { route: 'both' | 'single'; memberId?: string | null }, memberId: string): Promise<InboxItem>;
  /** Per-sender route override written by rerouteInboxItem, read by ingest
   *  before the category rule. Keyed by the full normalized sender address.
   *  Null = this sender has never been re-routed. */
  senderRouteOverride(senderKey: string): Promise<{ route: 'both' | 'single'; routedTo: string | null } | null>;
}

/* ---- 4c. Settings structure (card yQtaPZmm4vzI + IKK45inZ4vKE) ----
 *
 * Individual half lives on the member (a birthday is one person's), joint half
 * on the couple (an anniversary and the gift-lead times are shared). The
 * holiday list is what the gifting agent will read in a later card — this
 * card only stores and serves it.
 */

/** The fixed occasions — always present, always in this order. */
export const HOLIDAY_KEYS = [
  'birthday', 'anniversary', 'fathers-day', 'mothers-day', 'valentines',
] as const;

export type FixedHolidayKey = (typeof HOLIDAY_KEYS)[number];

/**
 * Custom occasions are numbered `custom-1`..`custom-N` by the DO in list
 * order, so the client never has to mint a unique key. `custom` on its own is
 * still ACCEPTED on input (it is what stored rows and older clients carry) and
 * is renumbered on the way through.
 */
export type HolidayKey = FixedHolidayKey | `custom-${number}`;
export const CUSTOM_KEY_RE = /^custom(-\d+)?$/;
export const MAX_CUSTOM_OCCASIONS = 5;
/** Custom occasion dates are month-and-day only: the occasion repeats yearly. */
export const MONTH_DAY_RE = /^\d{2}-\d{2}$/;

export interface HolidaySetting {
  key: HolidayKey;
  /** Empty until a custom occasion is named. */
  label: string;
  enabled: boolean;
  /** How far ahead the agent should start asking about gifts. */
  leadDays: number;
  /** Custom occasions only: MM-DD, repeats every year. Null until set. */
  date?: string | null;
}

/** Longest a real name or a chat display name may be (card yFLCjm7ONIEc).
 *  Both live in text columns with no CHECK, so the length rule is enforced on
 *  the write path — one number the worker, the DO and the Settings inputs all
 *  read, so the server's reason and the client's maxLength never disagree. */
export const NAME_MAX = 60;
export const DISPLAY_NAME_MAX = 60;

export interface MemberProfile {
  memberId: string;
  /** YYYY-MM-DD, or null when never set. */
  birthday: string | null;
  /** The chat name (members.display_name) — what the partner sees. Null only
   *  when the member row does not exist. */
  displayName: string | null;
  /** The reply-text colour key (members.reply_colour), null until picked. */
  replyColour: string | null;
}

/**
 * What the agent does with an inbox message that turns into a to-do:
 * `suggest` posts it for the pair to accept (the concierge default),
 * `auto` files it as an accepted to-do straight away. Off is not a mode here —
 * the `todos` AI permission is that switch.
 */
export type InboxTodoMode = 'suggest' | 'auto';
export const INBOX_TODO_MODES = ['suggest', 'auto'] as const;

export interface CoupleSettings {
  /** YYYY-MM-DD, or null when never set. */
  anniversary: string | null;
  holidays: HolidaySetting[];
  inboxTodoMode: InboxTodoMode;
  /** Task Assist (card to follow, spec kZX3gsJkKaTH): one couple-level
   *  consent switch for the chore-balance engine, plus whether the one-time
   *  offer still has to be shown. One row, so the two partners can never
   *  disagree about whether it is on. */
  taskAssist: TaskAssist;
}

export interface TaskAssist {
  /** Has the couple turned it on? Off until someone says yes — the engine
   *  reads this before it may surface anything. */
  enabled: boolean;
  /** Should the rail show the offer card now? True only while nobody has
   *  answered AND the couple has at least one completed chore to read. Once
   *  shown (or answered) it never comes back; Settings is the way in after. */
  offer: boolean;
}

export interface SettingsAPI {
  getMemberProfile(memberId: string): Promise<MemberProfile>;
  setMemberBirthday(memberId: string, birthday: string | null): Promise<MemberProfile>;
  /** The chat name (card yFLCjm7ONIEc). Writes members.display_name and tells
   *  both sockets the members list changed, so the partner sees it live. */
  setMemberDisplayName(memberId: string, displayName: string): Promise<MemberProfile>;
  /** Reply-text colour (card 9QADJv3CdiOh). Writes members.reply_colour and
   *  broadcasts 'members', same live path as the chat name. Throws
   *  'bad_reply_colour' for a key outside REPLY_COLOUR_KEYS. */
  setMemberReplyColour(memberId: string, colourKey: string): Promise<MemberProfile>;
  getCoupleSettings(): Promise<CoupleSettings>;
  setCoupleSettings(
    patch: {
      anniversary?: string | null;
      holidays?: HolidaySetting[];
      inboxTodoMode?: InboxTodoMode;
      /** Task Assist consent (spec kZX3gsJkKaTH). */
      taskAssist?: boolean;
      /** 'shown' stamps the one-time offer as delivered, 'no' means the
       *  couple declined it — both are permanent, neither re-asks. */
      taskAssistOffer?: 'shown' | 'no';
    },
    /** Who answered, for `enabled_by`. Optional so older callers still work. */
    memberId?: string | null,
  ): Promise<CoupleSettings>;
}

/* ---- 4d. Interests profile (card 0OvyJbpnACMX) ----
 *
 * One record per member: the four onboarding answers the agent asked for, and
 * the topic list those answers (or the model, or the person editing chips)
 * produced. The digest, the silent-tag subject branch and the gifting agent
 * all read this record — none of them invent their own profile.
 */

/** The four answers — bounded at INTEREST_ANSWER_MAX each, never truncated silently. */
export interface InterestAnswers {
  weekend: string;
  music: string;
  teams: string;
  movies: string;
}

export type InterestQuestionKey = keyof InterestAnswers;

/** The questionnaire as the agent asks it — copy lives with the contract. */
export const INTEREST_QUESTION_KEYS = ['weekend', 'music', 'teams', 'movies'] as const;

export const INTEREST_QUESTIONS: ReadonlyArray<{
  key: InterestQuestionKey;
  prompt: string;
  hint: string;
}> = [
  { key: 'weekend', prompt: 'What does a good weekend look like for you?', hint: 'Saturday coffee, a long run, the farmers market…' },
  { key: 'music', prompt: 'What are you listening to right now?', hint: 'Artists, albums, the playlist on repeat…' },
  { key: 'teams', prompt: 'Which teams do you follow?', hint: 'Codes, clubs, the group chat you check first…' },
  { key: 'movies', prompt: 'What do you watch when you want to switch off?', hint: 'Films, series, the comfort rewatch…' },
];

export const INTEREST_ANSWER_MAX = 300;
export const INTEREST_TOPICS_MAX = 12;
export const INTEREST_TOPICS_MAX_LEN = 30;

export interface MemberInterests {
  memberId: string;
  answers: InterestAnswers;
  /** Canonical display form, deduped, at most INTEREST_TOPICS_MAX. */
  topics: string[];
  /** All four answers given and at least one topic — the profile is usable. */
  completed: boolean;
  updatedAt: number;
}

/** What the partner may see of you: topics and progress, not your answers. */
export interface PartnerInterests {
  memberId: string;
  topics: string[];
  completed: boolean;
}

export interface InterestsAPI {
  getInterests(memberId: string): Promise<MemberInterests>;
  saveInterests(
    memberId: string,
    input: { answers: InterestAnswers; topics: string[] },
  ): Promise<MemberInterests>;
  /** Both members' profiles — the server-side reader for digest/gifting. */
  listInterests(): Promise<MemberInterests[]>;
  /**
   * Synchronous read used by the notebook scan's subject filter. Not wrapped
   * in serial(): it is a plain SELECT, and recordTagSuggestions already does
   * its validation reads the same way, outside the serial section.
   */
  interestsMatchSubject(subject: string): boolean;
}

/* ---- 4a2. Vault (card q9aZVIBKvyfy, V2 of BCDYLuzx6orJ) ---- */

/**
 * A vault entry a member sealed on the client. `photo` (V4) carries a data URL
 * sealed exactly like a letter body — the server still only ever sees `pv:v1:`
 * ciphertext, and the per-kind caps live in durable-object/vault.ts.
 */
export type VaultItemKind = 'letter' | 'secret' | 'photo';

/**
 * One sealed entry. `sealed` is `pv:v1:` framed ciphertext the server cannot
 * open; `dekWrap`/`dekIv` hold the per-item data key wrapped by the root key so
 * rotation re-wraps keys instead of re-encrypting history.
 */
export interface VaultItem {
  id: string;
  kind: VaultItemKind;
  sealed: string;
  dekWrap: string | null;
  dekIv: string | null;
  vrkVersion: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/** One member's wrapped copy of the couple root key, as stored server-side. */
export interface VaultStoredWrap {
  slot: 'a' | 'b';
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string;
  iv: string;
  ct: string;
}

/** Enrolment payload: exactly what shared/vault-crypto's wrap returns. */
export interface VaultMemberWrapInput {
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string;
  iv: string;
  ct: string;
}

/** The recovery wrap (V5, card 1CyNguq35PbV): the root key under HKDF(code, coupleId). */
export interface VaultStoredRecoveryWrap {
  kdf: 'HKDF-SHA256';
  /** base64 of the HKDF salt (the couple id) — non-secret, kept for verification. */
  salt: string;
  iv: string;
  ct: string;
}

/** Everything the unlock UI needs — no key material, only wraps. */
export interface VaultKeysView {
  vrkVersion: number;
  wraps: VaultStoredWrap[];
  /** Present only once an enrolment has issued a recovery code. */
  recovery: VaultStoredRecoveryWrap | null;
  /**
   * The slot belonging to the member who asked. The partner re-issue path needs
   * it: a member may only re-wrap the OTHER slot, and the client must not guess
   * its own from a member list whose order it does not control.
   */
  ownSlot: 'a' | 'b' | null;
  createdAt: number;
  rotatedAt: number | null;
}

export interface VaultAPI {
  /** Sealed rows only. The server never holds plaintext to return. */
  listVaultItems(memberId: string): Promise<VaultItem[]>;
  createVaultItem(
    memberId: string,
    input: {
      id?: string;
      kind: string;
      sealed: string;
      dekWrap?: string | null;
      dekIv?: string | null;
      vrkVersion?: number | null;
    },
  ): Promise<VaultItem>;
  deleteVaultItem(memberId: string, id: string): Promise<boolean>;
  getVaultKeys(memberId?: string): Promise<VaultKeysView | null>;
  /**
   * Enrol or re-wrap a member's copy of the root key. `targetSlot` is the
   * partner re-issue path (V5): one unlocked member stores a wrap for the OTHER
   * member's new passphrase. It may only replace a slot that is already
   * enrolled — an empty slot is joined, never claimed for someone else.
   */
  saveVaultMemberWrap(
    memberId: string,
    input: VaultMemberWrapInput,
    targetSlot?: 'a' | 'b',
  ): Promise<VaultKeysView>;
  /** Store the recovery wrap (V5). The server cannot open it: it needs the code. */
  saveVaultRecoveryWrap(memberId: string, input: VaultStoredRecoveryWrap): Promise<VaultKeysView>;
}

/* ---- 4b. Partner digest (card g3ixXFWzhgJY) ---- */

/** Times a week the receiver wants a digest. 0 = off. */
export const DIGEST_FREQUENCY_MAX = 7;
/** Jonathan, 2026-10-06: "2 to 4 instead of 3 to 5". */
export const DIGEST_STORIES_MIN = 2;
export const DIGEST_STORIES_MAX = 4;
/** A week, in ms — the window a frequency of X per week divides into. */
export const DIGEST_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** One story in the digest, already written in the concierge's voice. */
export interface DigestStory {
  title: string;
  /** 1-2 calm sentences. 'local' digests carry the article's own standfirst. */
  summary: string;
  url: string;
  source: string;
}

export interface PartnerDigest {
  id: string;
  /** Who receives it. */
  receiverId: string;
  /** Whose interests chose the stories. */
  sourceId: string;
  /** The interest topics the pull was built from — shown on the card. */
  topics: string[];
  /** The concierge's passage, synthesised ACROSS the stories (~75 words).
   *  Empty only on the local fallback, where the standfirsts are the copy. */
  body: string;
  /** The 2-4 stories cited underneath it. */
  stories: DigestStory[];
  /** 'model' = the concierge wrote the passage; 'local' = no key, standfirsts. */
  origin: 'model' | 'local';
  createdAt: number;
}

/** What GET /api/digest answers. */
export interface DigestState {
  /** X per week for this member, 0 = off. */
  frequency: number;
  /** The most recent digest built for this member, if any. */
  digest: PartnerDigest | null;
  /** True when frequency > 0 and the window has elapsed. */
  due: boolean;
  /** The partner's topics as they stand — empty means nothing to build from. */
  sourceTopics: string[];
}

export interface DigestAPI {
  getDigestFrequency(memberId: string): Promise<number>;
  setDigestFrequency(memberId: string, timesPerWeek: number): Promise<number>;
  /** Most recent digest for this receiver, or null. */
  latestDigest(memberId: string): Promise<PartnerDigest | null>;
  /** Due check: frequency > 0 and the last one is older than 7 days / X. */
  digestDue(memberId: string, now?: number): Promise<boolean>;
  saveDigest(input: {
    receiverId: string;
    sourceId: string;
    topics: string[];
    body: string;
    stories: DigestStory[];
    origin: 'model' | 'local';
  }): Promise<PartnerDigest>;
}

/* ---- 4a-bis. Weekly recap (card 3UwJvUtiirGN) ---- */

/** The recap window: one week, counted back from the send. */
export const RECAP_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Section caps — a recap is a page, not an archive. */
export const RECAP_QUESTIONS_MAX = 7;
export const RECAP_DECISIONS_MAX = 10;
export const RECAP_LINKS_MAX = 8;
/** Sends kept per couple: an audit of windows, not a mailbox. */
export const RECAP_KEEP_SENDS = 12;

/** What GET /api/recap answers. */
export interface RecapState {
  /** The 'recap' AI toggle — the opt-in, off for every couple by default. */
  enabled: boolean;
  /** True when enabled and the last send is older than a week (never sent = due). */
  due: boolean;
  /** When this couple's last recap went out, 0 = never. */
  lastSentAt: number;
  /** The window the next send would cover. */
  windowStart: number;
  windowEnd: number;
}

/** One answer to a daily question, as the questions slice stores it. */
export interface RecapAnswer {
  memberId: string;
  text: string;
  answeredAt: number;
}

/** A question whose answers fall inside the window (revealed or not). */
export interface RecapQuestion {
  questionId: string;
  prompt: string;
  state: 'pending' | 'revealed';
  revealedAt: number | null;
  answers: RecapAnswer[];
}

/** A request that moved this week — accepted, countered, declined, done. */
export interface RecapDecision {
  requestId: string;
  text: string;
  state: string;
  fromMemberId: string;
  toMemberId: string | null;
  counterText: string | null;
  updatedAt: number;
}

/** A link filed in the window. `shared: false` is the reader's own only. */
export interface RecapLink {
  itemId: string;
  title: string;
  url: string;
  shared: boolean;
  addedBy: string;
  createdAt: number;
}

export interface RecapCounts {
  questions: number;
  decisions: number;
  links: number;
}

/** The couple's own week, ready for the composer's privacy filter. */
export interface RecapActivity {
  windowStart: number;
  windowEnd: number;
  questions: RecapQuestion[];
  decisions: RecapDecision[];
  links: RecapLink[];
  /** member_id -> display name, both partners. */
  names: Record<string, string>;
}

export interface RecapAPI {
  /** Window arithmetic only — the DO is the only party that may say "is it time". */
  recapState(now?: number): Promise<{ lastSentAt: number; due: boolean; windowStart: number; windowEnd: number; nextDueAt: number }>;
  /** The couple's own week: questions answered, requests that moved, links filed. */
  recapActivity(windowStart: number, windowEnd: number): Promise<RecapActivity>;
  /** Record a send (counts + window only — no content is ever stored). */
  recordRecapSend(input: { windowStart: number; windowEnd: number; counts: RecapCounts }): Promise<number>;
}

/* ---- 4b. Gifts (card GCYLYcNZzUqL) ---- */

/** What a saved gift is filed under. Manual pick on the plain path; the
 *  agent-on path may overwrite it from one classify call. */
export const GIFT_CATEGORIES = ['other', 'tech', 'clothing', 'toys', 'books', 'food', 'home', 'experience'] as const;
export type GiftCategory = (typeof GIFT_CATEGORIES)[number];

export interface Gift {
  giftId: string;
  url: string;
  title: string;
  description: string;
  category: GiftCategory;
  /** 'agent' when the classify call wrote the row, else the hand-picked one. */
  categorySource: 'manual' | 'agent';
  recipient: string | null;
  occasion: string | null;
  notes: string | null;
  addedBy: string;
  /** Visibility (card 0WgqaZsYENJ6). false = 'just me': the saver's private
   *  idea — the concierge reads it server-side, the partner's queries never
   *  return it. Same 'me' / 'us' contract as a notebook card. */
  shared: boolean;
  createdAt: number;
  updatedAt: number;
}

/** What kind of gift a partner wants the agent to look for. Each member sets
 *  their own; the gifting agent uses it to pick recommendations (cards
 *  wYPNXGVzAb63 / MK8sHH7lfFDM / jqPcHtCfaE7K). */
export type GiftPreference = 'experience' | 'clothing' | 'meal' | 'anything';

/**
 * One member's private gift facts (card Yw5UFURMXSTY, PRD Epic 1.3). This IS
 * the PRD's "Alex will never see this": rows belong to the member who wrote
 * them, the partner's GET never returns them (404 addressing one by id), and
 * only the concierge reads them server-side when winnowing a pick FOR that
 * member.
 *
 *  - has_owned — the recipient already owns it, so it can never be the pick
 *  - not_taste — not their thing; excluded silently, never shown as a verdict
 *  - loved     — landed well; written by the T+5 reaction, kept as evidence
 */
export const GIFT_PRIVATE_KINDS = ['has_owned', 'not_taste', 'loved'] as const;
export type GiftPrivateKind = (typeof GIFT_PRIVATE_KINDS)[number];

export interface GiftPrivateRow {
  rowId: string;
  /** The member who may READ it — never the partner's id. */
  memberId: string;
  /** Who the row is ABOUT. A giver files "they already own one" under their
   *  own row so the partner never sees it, and tags the recipient — that tag
   *  is what the concierge filters on. Null = filed without a recipient. */
  forMemberId: string | null;
  kind: GiftPrivateKind;
  /** What the item is, in the owner's words. */
  label: string;
  createdAt: number;
}

/** The T+5 three-way reaction (PRD: loved = celebration + loved list,
 *  not-quite = a silent profile write, not-gifted = closes the ask with
 *  nothing recorded). No scoring of the giver, anywhere. */
export const GIFT_FEEDBACKS = ['loved', 'not_quite', 'not_gifted'] as const;
export type GiftFeedback = (typeof GIFT_FEEDBACKS)[number];

/* ---------------------------------------------------------- *
 *  Exit / re-pair carry-over (cards ocJz1TeLT4pi, O2J0Cm1ZFeNd)
 * ---------------------------------------------------------- */

/**
 * One member's private half of a room, as raw DB rows grouped by table.
 * The EXIT endpoint serialises exactly what the member owns and nothing the
 * couple shares. IMPORT re-files these rows into a *new* room under the new
 * memberId, regenerating the id columns so a second import cannot collide.
 *
 * Parsed, validated, never trusted: each table's row shape is checked on
 * import before a single INSERT runs, and the row just crosses the room
 * boundary with its timestamps preserved.
 */
export interface ExitBundle {
  /** Bump when the row shapes below change. Import refuses an unknown version. */
  version: 1;
  /** When the bundle was written (ms). Import preserves this for audit. */
  exportedAt: number;
  /** The member who owns every row in this bundle. */
  ownerMemberId: string;
  /** Private notebook items: shared=0 AND added_by = owner. */
  notebook: Record<string, unknown>[];
  /** Inbox items on the 'me' lane owned by the member. */
  inbox: Record<string, unknown>[];
  /** gift_private_lists rows with member_id = owner. */
  giftPrivate: Record<string, unknown>[];
  /** gift_preferences row for the owner, if any. */
  giftPreference: Record<string, unknown> | null;
  /** member_interests row for the owner, if any. */
  interests: Record<string, unknown> | null;
  /** member_profile birthday for the owner, if any. */
  birthday: string | null;
  /** question_answers rows authored by the owner. */
  questionAnswers: Record<string, unknown>[];
  /** requests SENT by the owner (from_member_id = owner). */
  requests: Record<string, unknown>[];
}

/** Days after acceptance before the reaction is asked (PRD "T+5"). */
export const GIFT_FEEDBACK_DELAY_DAYS = 5;

/** At-recommendation preference capture (card QltAysLlIuwm). Asking "already
 *  has / not their taste" UP FRONT is the burden the founder named — so the
 *  answer is asked ON the shown pick instead, one tap, and it files the same
 *  private profile rows the concierge already reads: `has_owned` and
 *  `not_taste` rule a pick out of the NEXT pass, `loved` feeds the ribbon.
 *  Distinct from the T+5 `GiftFeedback`, which is the post-purchase reaction. */
export const GIFT_PICK_FEEDBACKS = ['has_owned', 'not_taste', 'loved'] as const;
export type GiftPickFeedback = (typeof GIFT_PICK_FEEDBACKS)[number];

/** One-tap budget ceiling on the shopping pass. Prices only exist on rows a
 *  search returned, so the budget filters the search — a saved gift with no
 *  price is never dropped by a budget it never carried. */
export const GIFT_BUDGETS = ['any', 'under_50', 'under_100', 'under_200'] as const;
export type GiftBudget = (typeof GIFT_BUDGETS)[number];

/** One agent-produced gift suggestion, winnowed to a single pick per occasion
 *  (cards Mk8sHH7lfFDM / jqPcHtCfaE7K). "Accepted" copies occasion+recipient
 *  onto a saved gift; "re-steered" means the partner asked for a different
 *  category and the agent re-picked. */
export interface GiftRecommendation {
  recId: string;
  /** The occasion it's for: 'birthday-<memberId>', 'anniversary', 'fathers-day',
   *  'mothers-day', 'valentines' or 'custom-<n>'. */
  occasionKey: string;
  occasionLabel: string;
  /** The member the gift is FOR (birthdays). Null for a joint occasion. */
  forMemberId: string | null;
  /** Why this pick — shown under the recommendation card. */
  rationale: string;
  category: GiftCategory;
  /** The saved gift this recommendation points at, when one was picked. */
  giftId: string | null;
  giftTitle: string | null;
  giftUrl: string | null;
  status: 'pending' | 'accepted' | 're-nested';
  /** Who asked for this pick. Meaningful only for a private one. */
  addedBy: string | null;
  /** True when the pick is a 'just me' gift (card 0WgqaZsYENJ6): the row is
   *  then listed to its requester only, so a private idea can never describe
   *  itself to the partner. */
  isPrivate: boolean;
  /** Ordered flick-through stack (card Yw5UFURMXSTY): the search pass's
   *  linked picks, best first, so card 1 is the winnowed choice and the rest
   *  are what else came back. Empty until a search filled it — the winnow-to-
   *  one rule still stands, the stack is ordering, not exclusion. */
  stack: GiftEnrichmentPick[];
  /** 'Order by YYYY-MM-DD' — the occasion date minus that occasion's own
   *  leadDays, so "when to buy" is a date and not folklore (PRD delivery
   *  advisor, card Yw5UFURMXSTY). Null when the occasion is unknown. */
  orderBy: string | null;
  /** When it was accepted (reads as bought/planned) — the T+5 feedback clock
   *  runs from here, not from creation. Null while pending. */
  acceptedAt: number | null;
  /** Three-way reaction, asked once, five days after acceptance. Null = not
   *  answered; `feedbackDue` is derived on read, never stored. */
  feedback: GiftFeedback | null;
  feedbackAt: number | null;
  feedbackDue: boolean;
  /** At-recommendation capture (card QltAysLlIuwm) — which profile row the
   *  shown-pick tap filed. Null = not yet asked. One tap closes it, so a
   *  second tap on the same pick is refused rather than double-filed. The
   *  value is the FIRST verdict filed (kept for readers that only need
   *  "answered"); per-pick answers live in `pickFeedbacks`. */
  pickFeedback: GiftPickFeedback | null;
  pickFeedbackAt: number | null;
  /** Card ggTQe2KbB0A3 MEDIUM-2 — answers keyed by lowercased pick title, so
   *  a verdict on product one no longer paints product two's controls with
   *  "Noted". Empty object when nothing was answered (or before the column
   *  existed); a legacy row with only `pickFeedback` set reads as answered
   *  for whichever pick is showing. */
  pickFeedbacks?: Record<string, GiftPickFeedback>;
  createdAt: number;
  updatedAt: number;
}

/** One upcoming occasion the agent will start suggesting gifts for, with how
 *  far in advance it begins (the Settings lead-time field, card JKXJy2U9FVwx). */
export interface GiftOccasion {
  key: string;
  label: string;
  /** YYYY-MM-DD of the next occurrence. */
  date: string;
  /** How many days ahead the agent starts suggesting (0 when due today). */
  leadDays: number;
  forMemberId: string | null;
  due: boolean;
}

export interface GiftsAPI {
  /** Title + description + manual category land here on both paths; the
   *  agent path writes its classify result back afterwards via updateGift. */
  addGift(input: {
    url?: string;
    title: string;
    description?: string;
    category?: string;
    recipient?: string | null;
    occasion?: string | null;
    notes?: string | null;
    addedBy: string;
    /** 'just me' when false — private to addedBy, concierge-readable (0WgqaZsYENJ6). */
    shared?: boolean;
  }): Promise<Gift>;
  listGifts(viewerId: string): Promise<Gift[]>;
  /** Partial update — only fields present in the patch are touched, so a
   *  failed classify can never clobber a manual category. Pass `viewerId`
   *  for a member-initiated write: a 'just me' gift they do not own answers
   *  the same not_found as a missing id (card 0WgqaZsYENJ6). The agent path
   *  omits it and reads every row — that is the concierge's half of the
   *  contract. */
  updateGift(
    giftId: string,
    patch: {
      title?: string;
      description?: string;
      category?: string;
      categorySource?: 'manual' | 'agent';
      recipient?: string | null;
      occasion?: string | null;
      notes?: string | null;
    },
    viewerId?: string,
  ): Promise<Gift>;
  /** The partner's own gift preference (experience / clothing / meal /
   *  anything). Null = they have not answered yet. */
  getGiftPreference(memberId: string): Promise<GiftPreference | null>;
  setGiftPreference(memberId: string, preference: GiftPreference): Promise<GiftPreference>;
  /** The member's free-text budget maximum for the shopping pass (card
   *  suGPO1AGmKHj). Null = no ceiling. */
  getGiftBudgetMax(memberId: string): Promise<number | null>;
  setGiftBudgetMax(memberId: string, budgetMax: number | null): Promise<number | null>;
  /** The upcoming occasions the agent will suggest for, computed live from
   *  the couple's settings (birthday windows per member, anniversary, the
   *  fixed parent/valentine days, the custom occasion) using each one's lead
   *  time. Sorted by next date. */
  upcomingOccasions(now: number): Promise<GiftOccasion[]>;
  /** Winnow the couple's saved gifts to ONE suggestion for an occasion, using
   *  the FOR member's preference (their saved gifts of that kind first, then
   *  any). Never creates a duplicate pending recommendation for the same
   *  occasion — an existing pending pick is returned instead. */
  recommendGift(
    occasionKey: string,
    occasionLabel: string,
    forMemberId: string | null,
    viewerId: string,
    /** Card vm9sDf6VWc7S — the Brief step's audience control. Null/absent
     *  leaves privacy to the pick's own gift row (the shipped behaviour). */
    audience?: 'shared' | 'secret' | null,
  ): Promise<GiftRecommendation>;
  /** Pending recommendations for the viewer's couple. */
  listRecommendations(viewerId: string): Promise<GiftRecommendation[]>;
  /** Mark a recommendation accepted and copy its pick onto the saved gift
   *  (occasion + recipient), so the gift reads as bought/planned. */
  acceptRecommendation(recId: string, viewerId: string): Promise<GiftRecommendation>;
  /** The partner asked for a different category (or the same one again for a
   *  fresh pick) — the agent re-winnows to a new gift of that kind. */
  resteerRecommendation(recId: string, category: GiftCategory, viewerId: string): Promise<GiftRecommendation>;
  /** Rewrite a PENDING recommendation's rationale line with what a product
   *  search actually found (card GnfrAFZ6lBpM, Phase 2b). The search runs in
   *  the worker — the DO holds no key and must not await network inside
   *  serial() — so the row it enriches comes back through here. An accepted
   *  recommendation and an empty line are both returned untouched: a closed
   *  pick keeps the sentence that closed it, and "search found nothing" must
   *  never blank the honest 'Nothing saved yet fits'. */
  setRecommendationRationale(
    recId: string,
    rationale: string,
    viewerId: string,
  ): Promise<GiftRecommendation>;
  /** Store the shopping pass's linked picks as the ordered flick-through
   *  stack (card Yw5UFURMXSTY). Written with the rationale in the worker, so
   *  one search fills both; an empty list is a no-op. */
  setRecommendationStack(
    recId: string,
    picks: GiftEnrichmentPick[],
    viewerId: string,
  ): Promise<GiftRecommendation>;
  /** The viewer's own private rows (has_owned / not_taste / loved). The
   *  partner's list never carries them — same rule as a 'just me' gift. */
  listGiftPrivate(viewerId: string): Promise<GiftPrivateRow[]>;
  /** Add one private row for the viewer, tagged with who it is about (the
   *  recipient). Omitted -> the viewer's partner, which is what "just for
   *  them" means on the strip. */
  addGiftPrivate(input: { memberId: string; forMemberId?: string | null; kind: GiftPrivateKind; label: string }): Promise<GiftPrivateRow>;
  /** Delete one of the viewer's own rows; the partner's answers not_found. */
  removeGiftPrivate(rowId: string, viewerId: string): Promise<void>;
  /** The T+5 three-way reaction (card Yw5UFURMXSTY). 'loved' adds a loved
   *  row, 'not_quite' a silent not_taste row — filed BY the answerer and
   *  ABOUT the recipient, so the partner's list never carries them. Both
   *  are invisible to everyone but their owner. 'not_gifted' records
   *  nothing but the answer. A pick that is not accepted yet, or already
   *  answered, refuses. */
  giveRecommendationFeedback(
    recId: string,
    feedback: GiftFeedback,
    viewerId: string,
  ): Promise<GiftRecommendation>;
  /** At-recommendation capture (card QltAysLlIuwm): one tap on the SHOWN pick
   *  files the matching private profile row ABOUT the pick's recipient and
   *  stamps the pick answered. `label` is the pick title the tap was on —
   *  what the concierge will recognise next pass. */
  givePickFeedback(
    recId: string,
    verdict: GiftPickFeedback,
    label: string,
    viewerId: string,
  ): Promise<GiftRecommendation>;
  /** The `has_owned` / `not_taste` labels that rule a pick out for this
   *  recipient — the shopping pass reads them so a ruled-out product leaves
   *  the NEXT stack (server-side; the partner never sees a label). */
  ruledOutGiftLabels(memberId: string | null): Promise<string[]>;
  /** Due-check on read for the proactive occasion card (card Yw5UFURMXSTY):
   *  claims one window per occasion lead-window and returns the copy; the
   *  CoupleRoom wrapper posts the agent card and rolls the claim back if the
   *  Thread write fails — the digest / date-nudge shape, no cron. */
  giftOccasionNudge(input: { now: number }): Promise<{ sent: boolean; window: string | null; copy: string | null; reason?: string }>;
  /** Undo a claim whose Thread write never landed, so the next read retries. */
  releaseGiftNudge(windowKey: string): Promise<void>;
}

/* ---- 4b. Date Planner (card ZdYBVP203b8a, PRD v1 §5, §11 Phase A) ---- */

/** Lifecycle as PRD §5.2 stores it. The two AFFIRMED_BY_* steps are derived
 *  from date_answers rather than written as rows: an answer pair is the
 *  only thing that ever produces them, and deriving keeps state and answers
 *  from drifting apart. */
export const DATE_STATES = ['pool', 'suggested', 'locked', 'completed', 'raincheck'] as const;
export type DateState = (typeof DATE_STATES)[number];

export const DATE_SOURCES = [
  'agent', 'nomination_a', 'nomination_b', 'quick_add_a', 'quick_add_b', 'template',
] as const;
export type DateSource = (typeof DATE_SOURCES)[number];

/** Phase A has no Places API (PRD §11), so GO_OUT ideas only arrive from the
 *  couple's own capture — mode is still a first-class column for Phase B. */
export const DATE_MODES = ['stay_in', 'go_out', 'virtual'] as const;
export type DateMode = (typeof DATE_MODES)[number];

export const DATE_COST_BANDS = ['free', '$', '$$', '$$$'] as const;
export type DateCostBand = (typeof DATE_COST_BANDS)[number];

export const DATE_ENERGY = ['low', 'med', 'high'] as const;
export type DateEnergy = (typeof DATE_ENERGY)[number];

/** PRD §6.2a chips — one tap, never free text. 'other' carries the text. */
export const REVISE_REASONS = ['too_expensive', 'too_far', 'not_feeling_it', 'bad_timing', 'other'] as const;
export type ReviseReason = (typeof REVISE_REASONS)[number];

export const DATE_ANSWERS = ['affirm', 'revise'] as const;
export type DateAnswer = (typeof DATE_ANSWERS)[number];

/** PRD §11: calendar write on mutual affirm. 'not_connected' is a real,
 *  user-facing outcome (no Google grant yet), not an error. 'needs_time' is
 *  the other honest one: the couple is connected but the date has no `when`
 *  yet, so there is nothing a calendar could be told. */
export type DateCalendarStatus = 'none' | 'queued' | 'not_connected' | 'needs_time' | 'written' | 'failed';

export interface DateIdea {
  ideaId: string;
  title: string;
  note: string | null;
  source: DateSource;
  mode: DateMode;
  costBand: DateCostBand;
  energy: DateEnergy;
  placeName: string | null;
  placeUrl: string | null;
  /** PRD §5.1 placeRef — the Places identity behind a GO_OUT idea, plus the
   *  address the card shows. Null for curated templates and anything the
   *  couple typed themselves. Coordinates are deliberately NOT on the view:
   *  they exist for scoring only and never leave the box (pinned by
   *  scripts/probe-dates-prefs p12). */
  placeId: string | null;
  placeAddress: string | null;
  /** PRD §7 step 4 — why this card, in the concierge's voice. Templates keep
   *  their curated `note`; a Places card ships this instead. */
  explainer: string | null;
  /** Auto line for quick-adds ("from Maya's notebook, 12 Mar"). */
  provenance: string | null;
  state: DateState;
  templateKey: string | null;
  suggestedAt: number | null;
  lockedAt: number | null;
  completedAt: number | null;
  /** The date's actual start time (ms epoch), set at capture or later from
   *  the LOCKED card. null = no time yet, which is why the calendar write
   *  cannot happen on lock alone. */
  when: number | null;
  calendarStatus: DateCalendarStatus;
  calendarEventId: string | null;
  /** Last revise reason this idea drew — drives the next explainer line. */
  vetoReason: ReviseReason | null;
  rounds: number;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * What a partner is allowed to see (PRD §5.2 invariant: reactions are never
 * revealed to the other partner until LOCKED).
 *
 * `answers` is empty while the round is open — not "empty for the partner",
 * empty for everyone: the only thing a viewer ever gets pre-reveal is their
 * own answer back through `myAnswer`. Nothing here says whether the other
 * person has answered at all.
 */
export interface DateView extends DateIdea {
  myAnswer: DateAnswer | null;
  myReason: ReviseReason | null;
  revealed: boolean;
  /** Present only once `revealed` — { memberId, answer, reason } per member. */
  answers: { memberId: string; answer: DateAnswer; reason: ReviseReason | null }[];
  /** PRD §6.2a: the visible "we heard you" line, post-reveal only. */
  learnedNote: string | null;
}

export interface DateSuggestionResult {
  idea: DateView | null;
  /** PRD §9 guardrail: veto rate over half across 5+ rounds halts the agent. */
  halted: boolean;
  haltedReason: string | null;
  learnedNote: string | null;
  /** Phase B: what the Places run scored, when one happened at all. */
  place: DatePlaceOutcome | null;
}

/** One line of the PRD §7 step 3 ranking — numbers only, no geo, no address. */
export interface DatePlaceScoreLine {
  placesApiId: string;
  score: number;
  fit: number;
  fairness: number;
  novelty: number;
}

/** What the worker's Places search came back with, after the ladder. */
export interface DatePlaceOutcome {
  /** Everything that came back, scored, best first — the ranking the
   *  choice was made from (fairness and novelty asserted from here). */
  ranked: DatePlaceScoreLine[];
  /** Ladder rung that produced candidates: base | widen | relax. */
  step: string | null;
  /** The ONE soft constraint that rung dropped, named for the explainer. */
  relaxed: string | null;
  /** api_key | oauth | none — which credential actually carried the call. */
  credential: string;
  /** HTTP calls made (including credential retries) — the usage meter reads
   *  this, so a bill can never arrive unannounced. */
  attempts: number;
}

/**
 * Phase B: what the worker needs BEFORE it may call Places. Returned by the
 * DO so the network decision — and therefore the spend — happens in one
 * place. `query: null` means "do not call": `skip` says why, in words the
 * probe can assert on.
 */
export interface DateSuggestContext {
  openRound: boolean;
  halted: boolean;
  query: PlaceSearchQuery | null;
  skip: PlaceSkipReason | null;
  /** HTTP attempts already made for this couple today (daily cap input). */
  placesUsedToday: number;
}

/**
 * What the worker hands back with `suggestDateIdea`. Plain JSON on purpose —
 * it crosses the DO's RPC boundary, so nothing here may hold a binding.
 */
export interface DatePlaceSearch {
  candidates: PlaceCandidate[];
  /** Ladder rung that produced them (null = nothing came back). */
  step: string | null;
  relaxed: string | null;
  credential: string;
  attempts: number;
  reason: string | null;
  capped: boolean;
  /** Radius actually used, metres — the scorer measures fairness against it. */
  radiusM: number;
}

/** PRD verify line: "Places usage metered and reported before any bill". */
export interface DatePlacesUsage {
  /** Ladder runs recorded (a run can hold up to 3 HTTP attempts). */
  searches: number;
  httpAttempts: number;
  candidates: number;
  /** Estimate at USD 32/1,000 — a number to report, not a charge. */
  estCostUsd: number;
  usedToday: number;
  dailyMax: number;
  capped: boolean;
  lastAt: number | null;
}

/**
 * Card 2qBAZOM2LFWa — the pre-flight for a card-detail place search.
 * `origins` is null unless that half consented to precise location, exactly as
 * the date planner reads it; a card's own detail never overrides consent.
 */
export interface PlaceSearchContext {
  usedToday: number;
  dailyMax: number;
  origins: { a: GeoPoint | null; b: GeoPoint | null };
}

/* ------------------------------------------------------------------ *
 * PRD §6.4 — Preferences & consent (card sXseXczawWR1)
 * ------------------------------------------------------------------ */

export const DATE_LOCATION_CONSENTS = ['precise', 'city', 'off'] as const;
export type DateLocationConsent = (typeof DATE_LOCATION_CONSENTS)[number];

export const DATE_TIMINGS = ['weekday_eve', 'weekend_day', 'weekend_eve'] as const;
export type DateTiming = (typeof DATE_TIMINGS)[number];

/** A short, closed vocabulary — free text never reaches the agent scorer. */
export const DATE_CONSTRAINTS = ['near_transit', 'kid_friendly', 'vego', 'no_late_nights', 'outdoorsy'] as const;
export type DateConstraint = (typeof DATE_CONSTRAINTS)[number];

/** Per-factor tri-state attitude (card GMat5z0Zxutf). The decision factors
 *  ARE the constraint set — each gets Desirable / Neutral / Undesirable, and
 *  Undesirable is what lands back in `constraints` so the intersection and
 *  the scorer keep working on the exact rule-out list they already read. */
export const DATE_FACTOR_ATTITUDES = ['desirable', 'neutral', 'undesirable'] as const;
export type DateFactorAttitude = (typeof DATE_FACTOR_ATTITUDES)[number];

/** One member's saved row. Coordinates exist ONLY while consent is
 *  'precise'; 'city' keeps a label, 'off' keeps neither. */
export interface DateMemberPrefs {
  memberId: string;
  /** Dates per month the concierge aims for (1–4). */
  cadence: number;
  /** Weekday the suggestion should land by (0=Sun … 6=Sat). */
  suggestByDay: number;
  timing: DateTiming[];
  /** 0 = all stay-in, 100 = all go-out. */
  modeRatio: number;
  budget: DateCostBand;
  energy: DateEnergy;
  constraints: DateConstraint[];
  /** Per-factor tri-state (card GMat5z0Zxutf): Desirable / Neutral /
   *  Undesirable for each decision factor. `constraints` is derived from
   *  the Undesirable set, so the two can never disagree. */
  factorAttitudes: Record<DateConstraint, DateFactorAttitude>;
  locationConsent: DateLocationConsent;
  locationLabel: string | null;
  lat: number | null;
  lng: number | null;
  updatedAt: number;
}

/** prefs(A) ∩ prefs(B) — null-able fields mean the two said different
 *  things, i.e. there is no overlap to score against. */
export interface DatePrefsIntersection {
  cadence: number;
  suggestByDay: number | null;
  timing: DateTiming[];
  modeRatio: number | null;
  budget: DateCostBand;
  energy: DateEnergy | null;
  constraints: DateConstraint[];
  /**
   * Card GMat5z0Zxutf: factors BOTH members marked Desirable. Leaning is not
   * ruling out, so this is the union-free intersection — one partner's
   * preference never outvotes the other (same contract as every other field
   * here). The suggest path reads it to steer WHICH kind of place the
   * concierge looks for when neither member has ruled that factor out.
   */
  desirables: DateConstraint[];
}

export type DateDerivedMode = 'together' | 'long_distance' | 'unspecified';

/** What GET /api/dates/prefs returns. `mine` is the only object that can
 *  carry coordinates; the partner's copy is stripped to the consent level
 *  (PRD §6.4: "Your partner never sees where you are"). */
export interface DatePrefsView {
  mine: DateMemberPrefs;
  partner: DateMemberPrefs | null;
  /** Null until BOTH members have saved a row — one partner's set drives
   *  nothing, by design. */
  intersection: DatePrefsIntersection | null;
  derivedMode: DateDerivedMode;
  /** The banner's "how we know" line, verbatim. */
  derivedModeReason: string;
}

/**
 * One row of the date calendar outbox (the flush's unit of work). The queue
 * holds ids, an op and a counter only — the flush reads the idea row itself
 * for the title/time, so nothing couple-facing is duplicated here.
 */
export interface DateCalendarItem {
  ideaId: string;
  op: 'insert' | 'update';
  attempts: number;
  /** Google's id for this event, null until the first push succeeds. */
  externalId: string | null;
  /** The idea as it stands NOW — not as it stood when queued. */
  title: string;
  when: number;
  placeName: string | null;
  note: string | null;
}

export interface DateCalendarStats {
  /** Queued rows still under the retry ceiling. */
  pending: number;
  /** Rows that hit the ceiling and are parked until a time change re-enqueues them. */
  failed: number;
}

/* ---- 4y1. Date-idea proposals from the concierge (card qyhVbyMpcN0B) ---- */

/**
 * One proposal the scan accepts from the model: a title, the one-line
 * rationale that becomes the card's note, and the three closed-vocabulary
 * fields the scorer and the panel read. Anything else the model sends is
 * dropped at the parse boundary — the same rule the notebook scan applies
 * to themes.
 */
export interface DateIdeaProposal {
  title: string;
  note: string;
  mode: DateMode;
  costBand: DateCostBand;
  energy: DateEnergy;
}

export type DateIdeasScanStop =
  | 'toggle_off'
  | 'no_model_key'
  | 'no_signal'
  | 'pool_full'
  | 'model_error'
  /* Pear Plus (card 32BEnNO6I2kx): the date concierge is a Plus surface, so
     an Essentials couple's daily scan stops here — the same "stop reason is
     the answer" contract as the toggles. */
  | 'plus_required'
  | 'emitted';

export interface DateIdeasScanResult {
  scanned: boolean;
  reason: DateIdeasScanStop;
  /** Ideas persisted by this run (0 when the run stopped early). */
  count: number;
  /** Signal rows the prompt was built from (interests profiles, revealed
   *  game rounds, shared notebook items). */
  considered: number;
  /** Theme-tagged notebook links offered to the model this run (card
   *  TbzvQa3ZgXZO) — 0 whenever the Plus gate is closed or the couple has
   *  no themed link, which is the honest answer, not an error. */
  links: number;
}

/**
 * One shared, theme-tagged notebook LINK handed to the date-idea scan
 * (card TbzvQa3ZgXZO): "It could be surfaced via date recommendations eg
 * stay in and read". `theme` is the EDITORIAL label (notebookThemeLabel) so
 * the model reads human words; the raw key stays in the notebook where
 * search and filters need it. A candidate is an offer, never a claim: it
 * says nothing about whether anyone has read the link.
 */
export interface NotebookLinkCandidate {
  itemId: string;
  title: string;
  theme: string;
  createdAt: number;
}

/** Everything the daily date-idea scan needs to build one prompt. Plain
 *  reads, no serial(): the insert happens later, in its own serial(). */
export interface DateIdeasScanSeed {
  /** Topics per partner, both profiles flattened. */
  interests: string[];
  /** Revealed rounds only — an unrevealed answer never reaches a prompt. */
  game: { question: string; answers: string[] }[];
  /** Shared notebook entries (title + description), newest first. */
  notebook: string[];
  /** Theme-tagged shared notebook LINKS, newest first — the "stay in and
   *  read" candidates (card TbzvQa3ZgXZO). Empty when the Pear Plus gate is
   *  closed or the couple has no themed link; the rest of the seed stands. */
  linkCandidates: NotebookLinkCandidate[];
  /** Titles already in date_ideas, dismissed or not — the dedupe set. */
  knownTitles: string[];
  /** Dismissed titles, sent to the model as the avoid list. */
  dismissed: string[];
  poolCount: number;
  prefs: DatePrefsIntersection | null;
}

/** Lives on the DO itself (it needs env + the alarm), not on the slice —
 *  the same shape as NotebookScanAPI (card qyhVbyMpcN0B). */
export interface DateIdeasScanAPI {
  runDateIdeasScan(): Promise<DateIdeasScanResult>;
}

export interface DatesAPI {
  /** Vault quick-add / nomination → POOL (PRD §6.1). Capture is permissive. */
  captureDateIdea(input: {
    addedBy: string;
    title: string;
    note?: string | null;
    placeName?: string | null;
    placeUrl?: string | null;
    mode?: DateMode | null;
    costBand?: DateCostBand | null;
    energy?: DateEnergy | null;
    source?: DateSource | null;
    when?: number | null;
  }): Promise<DateView>;
  /** Every idea the couple owns, each rendered for `viewerId`. */
  listDateIdeas(viewerId: string): Promise<DateView[]>;
  /** Surface the next SUGGESTED idea: pool first, Phase B Places candidates
   *  second, then the template library. `now` is dev-only, same contract as
   *  tickRequests; `search` is the worker's Places result (see below). */
  /** `generate` (card gMiWQUTVrPSU) asks for a FRESH candidate: the pool pick
   *  is skipped so the ladder goes Places → template. An open round and a
   *  guardrail halt still win — one couple, one open card, one spend meter. */
  suggestDateIdea(
    viewerId: string,
    now?: number,
    search?: DatePlaceSearch | null,
    generate?: boolean,
  ): Promise<DateSuggestionResult>;
  /** Phase B, step 1 of a suggest: everything the worker must know BEFORE
   *  it may spend a call — an open round, a guardrail halt or a pool with
   *  ideas in it all answer `query: null` (with the reason) and no Places
   *  request is made. `generate: true` drops only `pool_has_ideas`, so a
   *  generate may go out and buy the call the saved pool would have blocked. */
  dateSuggestContext(viewerId: string, generate?: boolean): Promise<DateSuggestContext>;
  /** The meter behind "reported before any bill" (PRD §10 / card verify). */
  datePlacesUsage(): Promise<DatePlacesUsage>;
  /** Card 2qBAZOM2LFWa — pre-flight for a card-detail place search: today's
   *  count, the ceiling, and origins only where precise consent was given. */
  placeSearchContext(): Promise<PlaceSearchContext>;
  /** Card 2qBAZOM2LFWa — book one card-detail search against the same meter,
   *  and hand back the refreshed usage so the spend travels with the results. */
  recordPlaceSearch(search: DatePlaceSearch): Promise<DatePlacesUsage>;
  /** Send one pool idea into the loop ("Send to partner", PRD §6.2b). */
  promoteDateIdea(ideaId: string, viewerId: string): Promise<DateView>;
  /** Affirm or Revise. Both affirm → LOCKED + calendar write. Reveals answers. */
  answerDateIdea(input: {
    ideaId: string;
    memberId: string;
    answer: DateAnswer;
    reason?: ReviseReason | null;
    now?: number;
  }): Promise<DateView>;
  /** PRD §6.2c: close a locked date as completed, or raincheck it. */
  setOutcome(input: {
    ideaId: string;
    memberId: string;
    outcome: 'completed' | 'raincheck';
    now?: number;
  }): Promise<DateView>;
  /** PRD §9: a suggestion with 48h of silence dissolves back to POOL and its
   *  answers are dropped so nobody is anchored by a half-answered card. */
  dissolveSilentSuggestions(now: number): Promise<number>;
  /** Counters behind the PRD §9 guardrail, for the UI status line. */
  dateGuardrail(): Promise<{ rounds: number; vetoes: number; halted: boolean; message: string | null }>;
  /** PRD §7 cadence fit / §9 re-time rule (card yejoF8uTVxyf): the due-check
   *  that runs when the Dates surface is READ. Decides (and claims) one
   *  couple-addressed Thread nudge per local month, only inside a
   *  Thursday-evening or Monday slot; `now` and `offsetMinutes` come from the
   *  caller so the slot is the couple's local time and a dev probe can
   *  override the clock. The message itself is posted by the room wrapper,
   *  outside serial(). */
  dateCadenceNudge(input: { now: number; offsetMinutes: number }): Promise<DateNudgeResult>;
  /** Set (or move) the date's start time. On a LOCKED idea this re-queues the
   *  calendar write as op=update — the flush picks it up on the next pass. */
  setDateWhen(input: { ideaId: string; memberId: string; when: number }): Promise<DateView>;
  /** Queued calendar work, oldest first, only while the couple is connected.
   *  A row whose idea has no `when` stays queued — there is nothing to write. */
  dateCalendarBatch(limit?: number): Promise<DateCalendarItem[]>;
  dateCalendarAck(ideaId: string, externalId: string): Promise<void>;
  dateCalendarFail(ideaId: string, error: string): Promise<void>;
  dateCalendarStats(): Promise<DateCalendarStats>;
  /** PRD §6.4: the member's own prefs, the partner's stripped copy, the
   *  intersection (null until both saved) and the derived mode banner. */
  datePrefs(memberId: string): Promise<DatePrefsView>;
  /** Merge a partial update onto the member's row and return the view.
   *  A consent other than 'precise' deletes any stored coordinates. */
  saveDatePrefs(memberId: string, patch: Record<string, unknown>): Promise<DatePrefsView>;
  /** "Not for us" (card qyhVbyMpcN0B): park a POOL idea behind
   *  `dismissed_at` so it leaves every list and becomes part of the scan's
   *  avoid list. POOL only — anything further along the pipeline is a 409,
   *  because a locked date is a decision, not a proposal. */
  dismissDateIdea(ideaId: string, viewerId: string): Promise<DateView>;
  /** The prompt's raw material: interests, revealed game rounds, shared
   *  notebook items, the dedupe/avoid title sets and the prefs intersection.
   *  Plain reads — no serial(), the caller inserts in its own serial(). */
  dateIdeasScanSeed(opts?: { plus?: boolean }): Promise<DateIdeasScanSeed>;
  /** Persist a validated batch: clamped to 3, deduped against every known
   *  title (dismissed included), budget-filtered, state=pool/source=agent. */
  recordDateIdeas(input: { proposals: DateIdeaProposal[]; at: number }): Promise<number>;
}

/* ---- 4z. Plan (Life): the voice-first weekly meeting (card 7aTCRQ9j8PWV) ---- */

/** The four sections of the family-meeting structure (design v1.1 §5.6). */
export const PLAN_SECTION_KEYS = ['appreciation', 'week', 'fun', 'challenges'] as const;
export type PlanSectionKey = (typeof PLAN_SECTION_KEYS)[number];

/** What a drafted line becomes when the couple confirms it. */
export const PLAN_ITEM_KINDS = ['memory', 'task', 'request', 'thread'] as const;
export type PlanItemKind = (typeof PLAN_ITEM_KINDS)[number];

/** Section headings as the UI shows them — one source for labels and prompt. */
export const PLAN_SECTION_LABELS: Record<PlanSectionKey, string> = {
  appreciation: 'Appreciation',
  week: 'Week plans',
  fun: 'Fun ahead',
  challenges: 'Challenges to address',
};

/** Which kinds a section may file as (design §5.6): the model proposes, this
 *  clamps. Appreciation and fun are Notebook entries; week plans are tasks or
 *  requests; a challenge is a request or something to talk through in Thread. */
export const PLAN_SECTION_KINDS: Record<PlanSectionKey, readonly PlanItemKind[]> = {
  appreciation: ['memory'],
  week: ['task', 'request'],
  fun: ['memory'],
  challenges: ['request', 'thread'],
};

export interface PlanItem {
  /** Stable across edits so a confirmed line can be traced back to its draft. */
  id: string;
  section: PlanSectionKey;
  text: string;
  kind: PlanItemKind;
  /** Tasks only: 'me' is whoever recorded, 'partner' the other one. */
  assignee?: 'me' | 'partner' | 'anyone';
}

/**
 * One calendar entry the couple is asked to approve (card hCF9D2WCpv7Q).
 * Derived from a drafted line that names a day of the week, so the meeting
 * pays back into Dates/Calendar instead of staying a shelf of text. Nothing
 * reaches Google until somebody taps Add to calendar — `added` means a real
 * event was written, `dismissed` means the couple said no.
 */
export interface PlanCalendarDraft {
  id: string;
  text: string;
  /** The day the line named, 0=Sun … 6=Sat. */
  weekday: number;
  /** Suggested local hour, 0-23: read off "7pm" when present, else 19. */
  hour: number;
  status: 'proposed' | 'added' | 'dismissed';
  /** When it was approved, epoch ms in the approver's own timezone. */
  when: number | null;
  /** Google's id for the event, null until the push succeeds. */
  externalId: string | null;
  decidedBy: string | null;
  decidedAt: number | null;
}

export type PlanDraft = Record<PlanSectionKey, PlanItem[]> & {
  /** Proposals ride inside the stored draft: no extra table, and both
   *  partners read them from the same session row they already share. */
  calendar?: PlanCalendarDraft[];
};

/** One row a confirmed session created, kept on the session so history can
 *  show what each line became without reading four tables. */
export interface PlanSpawned {
  kind: PlanItemKind;
  id: string;
  section: PlanSectionKey;
  text: string;
}

export interface PlanSession {
  sessionId: string;
  status: 'draft' | 'confirmed' | 'discarded';
  transcript: string;
  transcriptLength: number;
  durationSeconds: number | null;
  draft: PlanDraft | null;
  spawned: PlanSpawned[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  confirmedAt: number | null;
}

/** The tasks table this card pulls forward from Phase 2. */
export interface PlanTask {
  taskId: string;
  title: string;
  assignee: 'me' | 'partner' | 'anyone';
  state: 'open' | 'done';
  /** The plan session that spawned it (design §6: source_event_id). */
  sourceEventId: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Us → "Something to share" (card a5fbGvPIggaR, founder email 2026-10-21).
 * Two independent gates on every row: `audience` decides who can SEE it (a
 * partner's `secret` rows never leave the Durable Object), `talk` decides
 * whether it is on the weekly meeting's agenda. Both must hold for a share to
 * reach the partner's meeting — a just-me share is never agenda material no
 * matter what the toggle says, which is why the UI refuses to arm the toggle
 * while the row is secret.
 */
export interface UsShare {
  shareId: string;
  text: string;
  /** "Let's talk about it" — put this on the weekly meeting's agenda. */
  talk: boolean;
  /** 'shared' = partner sees it, 'secret' = just me (default). */
  audience: 'shared' | 'secret';
  createdBy: string;
  createdAt: number;
}

/* ---- 4z. Billing — Pear Plus entitlement (card 32BEnNO6I2kx) ---- */

export interface BillingAPI {
  /** The couple's entitlement: plan, Stripe ids, status, concierge meter. */
  getEntitlement(): Promise<Entitlement>;
  /** Cheap gate read — yes/no, no meter. */
  isPlus(): Promise<boolean>;
  /** E1 (epic wPE7ApNqtqpa): the one record every payment source converges
   *  on — source, state, period end, last-verified-at. Derived from the same
   *  row isPlus() reads, so with the flag on the two answers are identical;
   *  with PEAR_PLUS_ENTITLEMENTS off, isPlus() is the expression it has
   *  always been and callers see no new field. */
  getEntitlementRecord(): Promise<EntitlementRecord>;
  /** Apply what Stripe said about the subscription (webhook / checkout). */
  applySubscription(input: SubscriptionUpdate): Promise<Entitlement>;
  /** Store the Stripe customer id on its own (checkout pre-step). */
  setCustomer(customerId: string): Promise<void>;
  /** One concierge turn: allowed?, running monthly count, allowance.
   *  `resvId` (card njDMEWNC4maU) makes the take IDEMPOTENT: the id is derived
   *  from the source message, so a retry after a restart finds the take that is
   *  already charged and returns it without advancing the meter. */
  takeConciergeTurn(now?: number, resvId?: string): Promise<ConciergeTurnResult>;
  /** Start the sign-up trial (idempotent, never shortens a live trial). */
  grantTrial(days: number, now?: number): Promise<Entitlement>;
  /** Pay one half of a referral (card 3MWZ8Zs7jA7e) — `grantId` is
   *  `<eventId>:friend` or `<eventId>:referrer`, and each half lands once. */
  grantReferralDays(input: { grantId: string; days: number }, now?: number): Promise<Entitlement>;
  /** The founder code's friend half (card NThh9EjYzupt): Plus until an
   *  ABSOLUTE end, not from a day count. The founder grant is "unlimited Plus
   *  expiring 2099-12-31", so it cannot ride the day arithmetic — a couple
   *  mid-trial would get 2099-12-31 plus their remaining trial days. Same
   *  ledger and same exactly-once promise as grantReferralDays: `grantId`
   *  names the half, and a repeat call changes nothing. The end only ever
   *  moves LATER (an existing later referral_end wins), so a founder grant
   *  never shortens coverage someone already holds. */
  grantPlusUntil(input: { grantId: string; until: number }, now?: number): Promise<Entitlement>;
  /** Redeem a promo code (card yWsCXHAXQ3Ej). Emails are the couple's
   *  account addresses — the code's allowlist is re-checked here, fail-closed.
   *  One grant per couple; throws promo_invalid / promo_wrong_account /
   *  promo_already_redeemed. */
  redeemPromo(input: { code: string; emails: readonly string[]; durationDays: number | null }, now?: number): Promise<Entitlement>;
  /** Revoke this couple's grant of `code`; true when a grant was cleared. */
  revokePromo(code: string, now?: number): Promise<boolean>;
  /** This couple's permanent promo redemption history (support/audit). */
  promoHistory(): Promise<{ code: string; grantedAt: number; revokedAt: number | null }[]>;
}

/* ---- 4za. Trial expiry reminders (card B5eG7xJj6egj) ----
 * Room-level (the sweep runs on the DO, not in the slice), so it lives
 * beside BillingAPI rather than inside it — the slice implements BillingAPI
 * and has no room identity, no alarm and no sweep of its own. */
export interface TrialRemindersAPI {
  /** The DO instance name IS the coupleId — the sweep's D1 address lookup. */
  coupleId(): string;
  /** One sweep: at most one notice, deduped on (trialEnd, offset). `now` is
   *  injectable for the probe; the alarm passes Date.now(). */
  runTrialReminders(now?: number): Promise<{
    now: number;
    skipped: string | null;
    trialEnd: number | null;
    due: number | null;
    daysOut: number | null;
    recipients: string[];
    sent: number[];
    /** Slice 3 of card HWrVePJ5qW4d — the push half of this sweep, its own
     *  dedupe and its own switch, independent of the mail transport. */
    push: { due: number; sent: number; skipped: string | null } | null;
  }>;
  /** Deliveries recorded for this trial end (the dedupe set). */
  trialReminderRows(trialEnd: number | null): { n: number; recipient: string | null; sentAt: number }[];
  /** Claim one mailbox's send before it happens — exactly one winner, with
   *  entitlement re-checked under the same lock. */
  claimTrialReminder(trialEnd: number, n: number, recipient: string | null, now?: number): Promise<'claimed' | 'already' | 'ineligible'>;
  /** Give a claim back when the transport refuses, so that mailbox retries. */
  releaseTrialReminder(trialEnd: number, n: number, recipient: string | null): void;
  /** Everything this couple has been sent (probe / support read-back).
   *  Returns a Promise: a synchronous method on the DO RPC boundary resolves
   *  to undefined, which is how this first failed. */
  trialReminderLog(): Promise<{ trialEnd: number; n: number; recipients: number; sentAt: number }[]>;
  /** When the daily alarm next fires — the schedule the sweep rides. */
  alarmAt(): Promise<number | null>;
}

export interface PlanAPI {
  /** Store the transcript; the draft arrives separately once the model answers. */
  createPlanSession(input: {
    transcript: string;
    durationSeconds?: number | null;
    memberId: string;
  }): Promise<PlanSession>;
  /** Save (or replace) the four-section draft. Empty sections are legal. */
  savePlanDraft(sessionId: string, draft: PlanDraft): Promise<PlanSession>;
  /** Newest first; transcripts come back truncated — history shows shape, not prose. */
  listPlanSessions(limit?: number): Promise<PlanSession[]>;
  getPlanSession(sessionId: string): Promise<PlanSession>;
  /** File every line the couple kept: one row per spawned item, each carrying
   *  source_event_id back to the session. `draft` is the edited version. */
  confirmPlanSession(input: {
    sessionId: string;
    memberId: string;
    draft?: PlanDraft | null;
  }): Promise<PlanSession>;
  discardPlanSession(sessionId: string): Promise<PlanSession>;
  /** Decide one calendar proposal (card hCF9D2WCpv7Q): `added` after the
   *  worker's own Google push succeeded (externalId comes back with it), or
   *  `dismissed`. The proposal is edited inside the stored draft JSON — a
   *  meeting's calendar drafts are part of the meeting, not a table of their
   *  own. Refuses a proposal that is already decided or a discarded session. */
  decidePlanCalendarDraft(input: {
    sessionId: string;
    draftId: string;
    status: 'added' | 'dismissed';
    memberId: string;
    when?: number | null;
    externalId?: string | null;
  }): Promise<PlanSession>;
  /** Tasks filed by confirmed meetings, open first. */
  listPlanTasks(): Promise<PlanTask[]>;
  /** Us "Something to share" (card a5fbGvPIggaR): store one share. `talk` is
   *  clamped off when the audience is secret — the meeting cannot hold a row
   *  the partner may not read. */
  addUsShare(input: {
    text: string;
    talk: boolean;
    audience: 'shared' | 'secret';
    memberId: string;
  }): Promise<UsShare>;
  /** Everything `viewerId` may see, newest first: their own shares in any
   *  audience, plus the partner's partner-visible ones. The partner's secret
   *  rows are filtered HERE, in the DO — the worker never receives them. */
  listUsShares(viewerId: string): Promise<UsShare[]>;
  /** The weekly meeting's agenda: partner-visible shares with `talk` on,
   *  oldest first (a talking point waits its turn). Both partners read the
   *  same list, so it is keyed on the audience, never on who asks. */
  listMeetingAgenda(): Promise<UsShare[]>;
}

/* ---- 5. The full DO surface children code against ---- */

export interface CoupleRoomFull extends CoupleRoomDO, RequestsAPI, ChoresAPI, GoogleConnectAPI, GoogleSyncAPI, GameAPI, GuessMeAPI, QuestionsAPI, AITogglesAPI, AgentSettingsAPI, NotebookAPI, NotebookScanAPI, InboxAPI, SettingsAPI, InterestsAPI, VaultAPI, DigestAPI, GiftsAPI, RecapAPI, DatesAPI, DateIdeasScanAPI, PlanAPI, BillingAPI, TrialRemindersAPI {}

export const COUPLE_INVARIANTS = [
  'members rows for a couple ≤ 2 (COUPLE_MAX_MEMBERS)',
  'messages.seq starts at 1 and is gapless within a couple',
  'messages.client_msg_id UNIQUE — a resent frame cannot double-insert',
  'no table, index or query names a coupleId other than this instance\'s own storage',
] as const;
